<script setup>
import { ref, watch, computed } from 'vue';
import { useStore } from 'vuex';
import { useClickAway } from '@/utils/useClickAway';
import MyLoadsDesktopButtonComponent from '@/components/myLoads/MyLoadsDesktopButtonComponent.vue';
import RadioButtonDesktopComponent from '@/components/RadioButtonDesktopComponent.vue';
import { useScreenSize } from '@/composables/useScreenSize';
import RadioButtonMobileComponent from '@/components/RadioButtonMobileComponent.vue';
import InputComponent from '@/components/InputComponent.vue';
import { useToast } from 'vue-toast-notification';
import DetailFieldDate from '@/components/DetailFieldDate.vue';
import FormField from '@/components/FormField.vue';
import { isPhone, formatPhone, isEmail } from '@/common/validators';
import usStates from '@/common/usStates';
import dayjs from 'dayjs';
import DropdownSelectComponent from '@/components/DropdownSelectComponent.vue';
import CustomCheckbox from '@/components/CustomCheckbox.vue';

const toast = useToast();
const store = useStore();

const props = defineProps({
  isLoadTrackable: {
    type: Boolean,
    required: true,
  },
  externalLoadId: {
    type: String,
    required: true,
  },
  loadId: {
    type: Number,
    required: false,
  },
  loadTicketId: {
    type: String,
    default: '',
  },
  origin: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  destination: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  workflowType: {
    type: String,
    default: 'assignVehicle',
  },
  equipmentVehicle: {
    type: Object,
    required: true,
    default: () => ({
      id: '',
      truck_number: '',
      vin: '',
    }),
  },
  assignedVehicle: {
    type: Object,
    required: true,
    default: () => ({
      id: '',
      truck_number: '',
      vin: '',
    }),
  },
  pendingVehicle: {
    type: Object,
    required: true,
    default: () => ({
      id: '',
      truck_number: '',
      vin: '',
    }),
  },
  assignedDriver: {
    type: Object,
    required: false,
    default: () => ({
      id: '',
      name: '',
      phone: '',
    }),
  },
  rateConId: {
    type: Number,
    required: false,
  },
  rateTerms: {
    type: String,
    required: false,
    default:
      'By signing this Rate Confirmation, Carrier agrees to transport the described shipment according to the terms herein. Carrier warrants it possesses all required operating authority, insurance coverage, and qualifications to legally perform the transportation services. Payment terms are net 30 days from receipt of complete and accurate delivery documentation, including BOL, POD, and invoice. Carrier shall immediately notify Broker of any service delays or exceptions. Carrier assumes full liability for cargo loss or damage while in possession, subject to the limitations set forth in 49 U.S.C. §14706. Double-brokering is strictly prohibited. Any accessorial charges must be pre-approved in writing. This agreement constitutes the entire understanding between parties regarding this shipment and supersedes all prior agreements. Disputes shall be resolved through arbitration in accordance with the transportation laws of the United States.',
  },
});

const fetchEquipmentTableVehicles = () => {
  store.dispatch('myLoadsStore/getEquipmentTableCarrierVehicles');
};

const fetchModalVehicles = () => {
  store.dispatch('myLoadsStore/getCarrierVehicles', { loadId: props.loadId });
};

fetchModalVehicles();

const fetchMyLoads = () => {
  store.dispatch('myLoadsStore/getMyLoads', 'unassigned');
  store.dispatch('myLoadsStore/getMyLoads', 'dispatched');
  store.dispatch('myLoadsStore/getMyLoads', 'in-transit');
  store.dispatch('myLoadsStore/getMyLoads', 'completed');
};

const isSetEldExemptFeatureEnabled = computed(
  () =>
    store.state.users.featureFlags
      ?.fe_carrier_set_carrier_vehicle_eld_exempt_status?.enabled
);

const isAssignDriverRateConFeatureEnabled = computed(
  () =>
    store.state.users.featureFlags?.fe_carrier_assign_driver_rate_con?.enabled
);

const isAssignVehicleRateConFeatureEnabled = computed(
  () =>
    store.state.users.featureFlags?.fe_carrier_assign_vehicle_rate_con?.enabled
);

const { screenSize } = useScreenSize();

const isDesktop = computed(() => screenSize.value === 'desktop');

const isLoadingVehicleList = computed(
  () => store.state.myLoadsStore.vehicles.isLoading
);

const assignVehicleFilteredVehicles = computed(() =>
  store.state.myLoadsStore.vehicles.allVehicles.filter(
    (vehicle) =>
      !vehicle.eldExemptCategory?.includes('pending') &&
      !vehicle.eldExemptCategory?.includes('denied')
  )
);

const rateConfirmationFilteredVehicles = computed(() =>
  store.state.myLoadsStore.vehicles.allVehicles.filter((vehicle) => {
    if (!vehicle.isEldExempt && !vehicle.isEldActive) {
      return false;
    } else if (
      (vehicle.isEldExempt && vehicle.eldExemptCategory?.includes('pending')) ||
      vehicle.eldExemptCategory?.includes('denied')
    ) {
      return false;
    } else {
      return true;
    }
  })
);

const allVehicles = computed(() =>
  props.workflow === 'rateCon'
    ? rateConfirmationFilteredVehicles.value
    : assignVehicleFilteredVehicles.value
);

const hasAllVehiclesLoaded = computed(
  () => store.state.myLoadsStore.vehicles.hasLoaded
);

const selectedVehicle = ref();

const selectedExemptVehicle = ref(props.equipmentVehicle);
const selectedDriver = ref(props.assignedDriver);
const eldExemptSelectedDriver = ref(null);
const selectedRelayVehicle = ref(null);
// for button spinners
const isLoadingSubmit = ref(false);
const submitSuccess = ref(false);
const submitError = ref(null);

const showInput = ref(!props.assignedVehicle.id && !props.pendingVehicle.id);
const filteredVehicles = ref([]);
const filteredRelayVehicles = ref([]);
const relayVehicles = computed(() =>
  allVehicles.value.filter(
    (vehicle) => vehicle.id !== selectedVehicle.value?.id
  )
);
const assignVehicleInput = ref('');
const assignRelayVehicleInput = ref('');

watch(
  () => allVehicles.value,
  (newVal) => {
    assignVehicleInput.value = '';
    assignRelayVehicleInput.value = '';
    selectedVehicle.value =
      newVal.find((vehicle) => vehicle.id === props.assignedVehicle.id) ||
      props.assignedVehicle;
    filteredVehicles.value = newVal;
    filteredRelayVehicles.value = relayVehicles.value;
  },
  { immediate: true, deep: true }
);

watch(
  relayVehicles,
  (newVal) => {
    assignRelayVehicleInput.value = '';
    filteredRelayVehicles.value = newVal;
  },
  { immediate: true, deep: true }
);

const modalRef = ref(null);
const emit = defineEmits([
  'closeModal',
  'openViewEquipmentModal',
  'updateAssignedVehicle',
]);
useClickAway(modalRef, () => emit('closeModal'));

const handleAssignVehicleInputChange = (value) => {
  assignVehicleInput.value = value;
  filteredVehicles.value = allVehicles.value.filter(
    ({ identifier, vin }) =>
      identifier
        .toString()
        .toLowerCase()
        .replace(/\s+/g, '')
        .includes(value.toString().toLowerCase().replace(/\s+/g, '')) ||
      vin
        .toString()
        .toLowerCase()
        .replace(/\s+/g, '')
        .includes(value.toString().toLowerCase().replace(/\s+/g, ''))
  );
};

const handleAssignRelayVehicleInputChange = (value) => {
  assignRelayVehicleInput.value = value;
  filteredRelayVehicles.value = relayVehicles.value.filter(
    ({ identifier, vin }) =>
      identifier
        .toString()
        .toLowerCase()
        .replace(/\s+/g, '')
        .includes(value.toString().toLowerCase().replace(/\s+/g, '')) ||
      vin
        .toString()
        .toLowerCase()
        .replace(/\s+/g, '')
        .includes(value.toString().toLowerCase().replace(/\s+/g, ''))
  );
};

const handleAssignVehicleEnterPress = () => {
  if (filteredVehicles.value.length === 1) {
    const vehicle = filteredVehicles.value[0];
    if (vehicle.id === selectedVehicle.value?.id) {
      handleAssignVehicleContinueClick();
      return;
    }
    handleAssignVehicleRadioButtonClick(vehicle);
  }
};

const handleAssignRelayVehicleEnterPress = () => {
  if (filteredRelayVehicles.value.length === 1) {
    const vehicle = filteredRelayVehicles.value[0];
    if (vehicle.id === selectedRelayVehicle.value?.id) {
      handleAssignRelayVehicleContinueClick();
      return;
    }
    handleAssignRelayVehicleRadioButtonClick(vehicle);
  }
};

const handleAssignVehicleRadioButtonClick = (vehicle) => {
  selectedVehicle.value = {
    id: vehicle.id,
    truck_number: vehicle.identifier,
    vin: vehicle.vin,
    isEldActive: vehicle.isEldActive,
    isEldExempt: vehicle.isEldExempt,
    eldExemptCategory: vehicle.eldExemptCategory,
  };
};

const handleAssignRelayVehicleRadioButtonClick = (vehicle) => {
  selectedRelayVehicle.value = {
    id: vehicle.id,
    truck_number: vehicle.identifier,
    vin: vehicle.vin,
    isEldActive: vehicle.isEldActive,
    isEldExempt: vehicle.isEldExempt,
    eldExemptCategory: vehicle.eldExemptCategory,
  };
};

const isAssignDriverMyLoadsFeatureEnabled = computed(
  () =>
    store.state.users.featureFlags?.fe_carrier_assign_driver_my_loads?.enabled
);

const handleAssignVehicleContinueClick = () => {
  if (
    !selectedVehicle.value?.isEldActive &&
    !selectedVehicle.value?.isEldExempt
  ) {
    if (isSetEldExemptFeatureEnabled.value) {
      selectedExemptVehicle.value = selectedVehicle.value;
      if (props.workflowType === 'assignVehicle') {
        currentStepIndex.value = currentStepIndex.value + 2;
        return;
      }
      goForward();
      return;
    }
    toast.open({
      message: 'Please select an ELD active or ELD exempt vehicle to assign.',
      type: 'error',
      position: 'top-right',
      duration: 2000,
    });
    return;
  }
  if (
    selectedVehicle.value?.isEldExempt &&
    props.workflowType === 'assignVehicle' &&
    isAssignDriverMyLoadsFeatureEnabled.value
  ) {
    fetchDrivers();
    goForward();
    return;
  } else if (props.workflowType === 'assignVehicle') {
    // skip assign driver plus eld exempt steps
    currentStepIndex.value = currentStepIndex.value + 5;
    return;
  }
  if (allVehicles.value.length > 1) {
    // open relay modal prompt
    if (props.workflowType === 'rateCon') {
      goForward();
      return;
    }
    skipEldExemptStep();
  } else {
    //skip relay prompt
    assignVehicle();
  }
};

const handleAssignRelayVehicleContinueClick = () => {
  if (
    !selectedRelayVehicle.value.isEldActive &&
    !selectedRelayVehicle.value.isEldExempt
  ) {
    if (isSetEldExemptFeatureEnabled.value) {
      selectedExemptVehicle.value = selectedRelayVehicle.value;
      goForward();
      return;
    }
    toast.open({
      message: 'Please select an ELD active or ELD exempt vehicle to assign.',
      type: 'error',
      position: 'top-right',
      duration: 2000,
    });
    return;
  }
  if (props.workflowType === 'rateCon') {
    goForward();
    return;
  }
  // show relay form and assign button
  skipEldExemptStep();
};

const handleAssignPickupClick = () => {
  if (
    selectedVehicle.value?.isEldExempt &&
    props.isLoadTrackable &&
    selectedVehicle.value?.eldExemptCategory?.includes(
      'driver_does_not_travel_over_150_miles'
    )
  ) {
    goForward();
    return;
  } else if (props.workflowType === 'rateCon') {
    // got ot assignTrailer
    currentStepIndex.value = currentStepIndex.value + 4;
    return;
  }
  assignVehicle();
};

const assignVehicle = async () => {
  isLoadingSubmit.value = true;
  // showAssignVehicleModal.value = false;
  if (
    selectedVehicle.value?.id?.length === 0 ||
    !props.loadId?.toString().length
  ) {
    toast.open({
      message: 'Please select a vehicle with valid id to assign.',
      type: 'error',
      position: 'top-right',
      duration: 2000,
    });
    return;
  }
  const payload = {
    carrier_vehicle_id: selectedVehicle.value?.id,
  };
  if (selectedRelayVehicle.value?.id && pickupDateTimeUtcIsoString.value) {
    payload.relays = [
      {
        inbound_carrier_vehicle_id: selectedVehicle.value?.id,
        outbound_carrier_vehicle_id: selectedRelayVehicle.value.id,
        switch_at: pickupDateTimeUtcIsoString.value,
      },
    ];
  }
  const res = await store.dispatch('myLoadsStore/patchMyLoad', {
    id: props.loadId,
    payload: payload,
  });
  isLoadingSubmit.value = false;
  if ([403, 422].includes(res.status)) {
    toast.open({
      message: `Error: ${res.status === 403 ? 'Not Authorized to update vehicle' : res.response?.data?.error || 'Assigned vehicle not updated. Please try again.'}`,
      type: 'error',
      position: 'top-right',
      duration: 5000,
    });
    return;
  }
  if ([200, 201, 204].includes(res.status)) {
    emit('updateAssignedVehicle', selectedVehicle.value);
    fetchMyLoads();
    toast.open({
      message:
        'Assigned vehicle successfully updated to ' +
          selectedVehicle.value.truck_number || selectedVehicle.value.vin,
      position: 'top-right',
      duration: 1500,
    });
    if (selectedRelayVehicle.value?.id) {
      emit('updateAssignedRelayVehicle', selectedRelayVehicle.value);
      toast.open({
        message:
          'Assigned relay vehicle successfully updated to ' +
            selectedRelayVehicle.value.truck_number ||
          selectedRelayVehicle.value.vin,
        position: 'top-right',
        duration: 1500,
      });
    }
    emit('closeModal');
    if (
      selectedVehicle.value.isEldExempt &&
      eldExemptSelectedDriver.value?.id
    ) {
      // make call to add driver to load
      const payload = {
        driver_user_id: eldExemptSelectedDriver.value.id,
      };
      try {
        const res = await store.dispatch('myLoadsStore/addDriverToLoad', {
          id: props.loadId,
          payload: payload,
        });
        if ([200, 201, 204].includes(res.status)) {
          toast.open({
            message: 'Driver added to load successfully',
            type: 'success',
            position: 'top-right',
            duration: 1500,
          });
        } else {
          throw new Error('Failed to add driver to load');
        }
      } catch (error) {
        console.error('error: ', error);
        toast.open({
          message: 'Error adding driver to load. Please try again.',
          type: 'error',
          position: 'top-right',
          duration: 5000,
        });
      }
    }
  } else {
    if (
      selectedVehicle.value.isEldExempt &&
      eldExemptSelectedDriver.value?.id
    ) {
      // make call to add driver to load
      const payload = {
        driver_user_id: eldExemptSelectedDriver.value.id,
      };
      try {
        const res = await store.dispatch('myLoadsStore/addDriverToLoad', {
          id: props.loadId,
          payload: payload,
        });
        if ([200, 201, 204].includes(res.status)) {
          toast.open({
            message: 'Driver added to load successfully',
            type: 'success',
            position: 'top-right',
            duration: 1500,
          });
        } else {
          throw new Error('Failed to add driver to load');
        }
      } catch (error) {
        console.error('error: ', error);
        toast.open({
          message: 'Error adding driver to load. Please try again.',
          type: 'error',
          position: 'top-right',
          duration: 5000,
        });
      }
    }
    toast.open({
      message: `Error: ${res.response?.data?.error || 'Assigned vehicle not updated. Please try again.'}`,
      type: 'error',
      position: 'top-right',
      duration: 5000,
    });
  }
};

const submitEldExemptClaim = async () => {
  // Reset states
  const exemptReason = prepareExemptionData();

  submitSuccess.value = false;
  submitError.value = null;

  if (!selectedExemptVehicle.value?.id || !exemptReason?.id) {
    const errorMessage =
      'Unable to update ELD exempt status. Please try again.';
    submitError.value = errorMessage;
    isLoadingSubmit.value = false;
    toast.open({
      message: errorMessage,
      type: 'error',
      position: 'top-right',
      duration: 2000,
    });
    return;
  }

  let payload;

  if (exemptReason.documents) {
    payload = exemptReason.documents;
    payload.append('carrier_vehicle_id', selectedExemptVehicle.value.id);
    payload.append('eld_exemption_type', exemptReason.id);
    payload.append('address', address.value.trim());
    payload.append('user_id', store.state.users.currentUser.id);
  } else {
    payload = {
      carrier_vehicle_id: selectedExemptVehicle.value.id,
      eld_exemption_type: exemptReason.id,
      address: address.value.trim(),
      user_id: store.state.users.currentUser.id,
    };
  }

  isLoadingSubmit.value = true;

  let res = null;
  try {
    res = await store.dispatch(
      'myLoadsStore/postCarrierVehicleEldExemptStatus',
      { payload }
    );
    isLoadingSubmit.value = false;
  } catch (error) {
    const errorMessage =
      error.status == 403
        ? 'Not Authorized to update ELD exempt status'
        : 'Unable to update ELD exempt status. Please try again.';
    submitError.value = errorMessage;
    isLoadingSubmit.value = false;
    toast.open({
      message: `Error: ${errorMessage}`,
      type: 'error',
      position: 'top-right',
      duration: 2000,
    });
    return;
  }

  if ([200, 201, 204].includes(res.status)) {
    fetchEquipmentTableVehicles();
    submitSuccess.value = true;
    toast.open({
      message:
        res.message ||
        `Carrier vehicle ${selectedVehicle.value.truck_number || ''} eld exempt status saved!`,
      position: 'top-right',
      duration: 1500,
    });
  } else {
    const errorMessage =
      'Unable to update ELD exempt status. Please try again.';
    submitError.value = errorMessage;
    isLoadingSubmit.value = false;
    toast.open({
      message: errorMessage,
      type: 'error',
      position: 'top-right',
      duration: 2000,
    });
  }
};

const timezoneRegions = [
  'America/Chicago',
  'America/New_York',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
];

const now = new Date();

const selectedTimeDate = ref(now);

const timezones = computed(() => {
  const options = [];
  timezoneRegions.forEach((region) => {
    const label = new Intl.DateTimeFormat('en-US', {
      timeZone: region,
      timeZoneName: 'short',
    })
      .formatToParts(selectedTimeDate.value)
      .find((part) => part.type === 'timeZoneName').value;
    if (!options.map((tz) => tz.label).includes(label)) {
      options.push({ region: region, label: label });
    }
  });
  return options;
});

const defaultTimeInParts = computed(() => {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: props.origin?.timezone,
    timeZoneName: 'short',
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  }).formatToParts(selectedTimeDate.value);
});

const defaultTimezone = computed(
  () =>
    defaultTimeInParts.value.find((part) => part.type === 'timeZoneName')?.value
);

const selectedTimezone = ref(
  timezones.value.find((tz) => tz.label === defaultTimezone.value)?.region ||
    timezones.value[0]?.region
);

const stopTime = computed(() =>
  dayjs(props.origin?.stopDateRaw).tz(props.origin?.timezone)
);

const pickupDate = ref(stopTime.value);

const pickupDateTimeUtcIsoString = ref('');

// Watch both pickupDate and selectedTimezone to update the ISO string
watch(
  [pickupDate, selectedTimezone],
  () => {
    // Get raw date/time components and create a string
    const year = pickupDate.value.year();
    const month = pickupDate.value.month() + 1; // dayjs months are 0-indexed
    const date = pickupDate.value.date();
    const hour = pickupDate.value.hour();
    const minute = pickupDate.value.minute();
    const second = pickupDate.value.second();

    // Create ISO string format: YYYY-MM-DDTHH:mm:ss
    const dateTimeString = `${year}-${month.toString().padStart(2, '0')}-${date.toString().padStart(2, '0')}T${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:${second.toString().padStart(2, '0')}`;

    // Convert the raw time to the selected timezone
    const dateInTimezone = dayjs.tz(dateTimeString, selectedTimezone.value);
    pickupDateTimeUtcIsoString.value = dateInTimezone.toISOString();
  },
  { immediate: true }
);

const handleSetPickupDate = (date) => {
  pickupDate.value = date;
};

const showAddressForm = ref(false);

// const checkBoxChecked = ref(false);

// steps flow

const currentStepIndex = ref(0);

const goForward = () => {
  currentStepIndex.value++;
};

const skipEldExemptStep = () => {
  currentStepIndex.value = currentStepIndex.value + 4;
};

const goBack = () => {
  currentStepIndex.value--;
};

const handleEldExemptTypeSaveClick = () => {
  if (
    selectedExemptReason.value.id === 'driver_does_not_travel_over_150_miles' &&
    !showAddressForm.value
  ) {
    showAddressForm.value = true;
  } else {
    submitEldExemptClaim();
    goForward();
  }
};

const assignDriverNameInput = ref('');

const eldExemptAssignDriverFirstNameInput = ref('');

const handleEldExemptAssignDriverFirstNameInputChange = (value) => {
  eldExemptAssignDriverFirstNameInput.value = value.trim();
};

const eldExemptAssignDriverLastNameInput = ref('');

const handleEldExemptAssignDriverLastNameInputChange = (value) => {
  eldExemptAssignDriverLastNameInput.value = value.trim();
};

const assignDriverPhoneInput = ref('');

const eldExemptAssignDriverPhoneInput = ref('');

const assignDriverPhoneNumber = ref('');

const eldExemptAssignDriverPhoneNumber = ref('');

const handleAssignDriverNameInputChange = (value) => {
  assignDriverNameInput.value = value.trim();
};

const phoneError = ref('');

const formFieldRef = ref(null);

const eldExemptFormFieldRef = ref(null);

const countryCode = ref('');

const eldExemptPhoneError = ref('');

const eldExemptCountryCode = ref('');

const handleAssignDriverPhoneInputChange = (e) => {
  countryCode.value =
    formFieldRef.value?.elementData?.selectedCountryData?.iso2;
  const value = e.target.value;
  assignDriverPhoneInput.value = formatPhone(value);
};

const handleEldExemptAssignDriverPhoneInputChange = (e) => {
  eldExemptCountryCode.value =
    eldExemptFormFieldRef.value?.elementData?.selectedCountryData?.iso2;
  const value = e.target.value;
  eldExemptAssignDriverPhoneInput.value = formatPhone(value);
};

const getPhoneError = () => {
  if (
    (['us', 'mx', 'ca'].includes(countryCode.value) &&
      assignDriverPhoneInput.value.length !== 12) ||
    !formFieldRef.value?.elementData?.isValidNumber()
  ) {
    return `Please enter a valid ${countryCode.value.toUpperCase()} phone number.`;
  } else if (
    assignDriverPhoneInput.value.length === 12 &&
    !isPhone(assignDriverPhoneInput.value)
  ) {
    return `Please enter a valid ${countryCode.value.toUpperCase()} phone number.`;
  }
  return '';
};

const getEldExemptPhoneError = () => {
  if (
    (['us', 'mx', 'ca'].includes(eldExemptCountryCode.value) &&
      eldExemptAssignDriverPhoneInput.value.length !== 12) ||
    !eldExemptFormFieldRef.value?.elementData?.isValidNumber()
  ) {
    return `Please enter a valid ${eldExemptCountryCode.value.toUpperCase()} phone number.`;
  } else if (
    eldExemptAssignDriverPhoneInput.value.length === 12 &&
    !isPhone(eldExemptAssignDriverPhoneInput.value)
  ) {
    return `Please enter a valid ${eldExemptCountryCode.value.toUpperCase()} phone number.`;
  }
  return '';
};

const handleCountrySelectChange = () => {
  countryCode.value =
    formFieldRef.value?.elementData?.selectedCountryData?.iso2;
  if (assignDriverPhoneInput.value.length > 0) {
    eldExemptPhoneError.value = getPhoneError();
  }
  eldExemptPhoneError.value = '';
};

const handleEldExemptAssignDriverCountrySelectChange = () => {
  eldExemptCountryCode.value =
    eldExemptFormFieldRef.value?.elementData?.selectedCountryData?.iso2;
  if (eldExemptAssignDriverPhoneInput.value.length > 0) {
    eldExemptPhoneError.value = getEldExemptPhoneError();
  }
  eldExemptPhoneError.value = '';
};

let timeoutId = null;

watch(assignDriverPhoneInput, (newVal) => {
  assignDriverPhoneNumber.value = formFieldRef.value?.elementData?.getNumber();
  // clear exisiting errors
  phoneError.value = '';
  if (newVal) {
    if (newVal.length === 12) {
      phoneError.value = getPhoneError();
      return;
    }
    // if value is not empty, debounce for displaying phone errors
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      phoneError.value = getPhoneError();
    }, 3000);
  }
});

let eldExemptTimeoutId = null;

watch(eldExemptAssignDriverPhoneInput, (newVal) => {
  eldExemptAssignDriverPhoneNumber.value =
    eldExemptFormFieldRef.value?.elementData?.getNumber();
  // clear exisiting errors
  eldExemptPhoneError.value = '';
  if (newVal) {
    if (newVal.length === 12) {
      eldExemptPhoneError.value = getEldExemptPhoneError();
      return;
    }
    // if value is not empty, debounce for displaying phone errors
    if (eldExemptTimeoutId) {
      clearTimeout(eldExemptTimeoutId);
    }
    eldExemptTimeoutId = setTimeout(() => {
      eldExemptPhoneError.value = getEldExemptPhoneError();
    }, 3000);
  }
});

// Set up clickaway for phone field validation
const handlePhoneFieldBlur = () => {
  if (timeoutId) {
    clearTimeout(timeoutId);
  }
  phoneError.value = getPhoneError();
};

const handleEldExemptPhoneFieldBlur = () => {
  if (eldExemptTimeoutId) {
    clearTimeout(eldExemptTimeoutId);
  }
  eldExemptPhoneError.value = getEldExemptPhoneError();
};

const isAssignDriverButtonDisabled = computed(() => {
  if (
    assignDriverNameInput.value.trim() === '' ||
    assignDriverPhoneInput.value.trim() === '' ||
    phoneError.value
  ) {
    return true;
  }
  return false;
});

const assignTrailerNumberInput = ref('');

const handleAssignTrailerNumberInputChange = (value) => {
  assignTrailerNumberInput.value = value.toString().trim();
};

const isAssignVehicleButtonDisabled = computed(() => {
  return selectedVehicle.value?.id ? false : true;
});

const isAssignRelayVehicleButtonDisabled = computed(() => {
  return selectedRelayVehicle.value?.id ? false : true;
});

const isTermsAndConditionsCheckboxChecked = ref(false);

const isAcceptTermsButtonDisabled = computed(() =>
  isTermsAndConditionsCheckboxChecked.value ? false : true
);

const relayPickupTimePrimaryButtonText = computed(() =>
  props.workflowType === 'rateCon' ? 'Continue' : 'Assign'
);

const handleRelayPickupTimeContinueClick = () => {
  if (props.workflowType === 'rateCon') {
    goForward();
    return;
  }
  assignVehicle();
};

const isEldExemptAttestationCheckboxChecked = ref(false);

const isRelayPromptSecondaryButtonText = computed(() =>
  props.workflowType === 'rateCon' ? 'No, Continue' : 'No, Assign'
);

const handleAssignTrailerCancelClick = () => {
  if (selectedRelayVehicle.value) {
    goBack();
    return;
  }
  if (
    selectedVehicle.value?.isEldExempt &&
    props.isLoadTrackable &&
    selectedVehicle.value?.eldExemptCategory?.includes(
      'driver_does_not_travel_over_150_miles'
    )
  ) {
    currentStepIndex.value = currentStepIndex.value - 2;
    return;
  }
  currentStepIndex.value = currentStepIndex.value - 4;
};

const selectedExemptReason = ref(null);

const fileOverDropArea = ref(false);
const selectedFiles = ref([]);

const handleExemptReasonSelect = (reason) => {
  if (reason.id !== 'driver_does_not_travel_over_150_miles') {
    showAddressForm.value = false;
  }
  selectedExemptReason.value = reason;
  if (selectedFiles.value) {
    selectedFiles.value = [];
  }
};

const formsLookup = {
  driver_does_not_travel_over_150_miles: {
    title: 'Upload Documentation',
    description:
      'To request this exemption, you must provide all of the following documents for the last 7 days:',
    bulletPoints: [
      'The time the driver reports for duty each day',
      'The total number of hours on duty each day',
      'The time the driver is released from duty each day',
    ],
  },
  vehicle_has_glider_kit: {
    title: 'Upload Documentation',
    description:
      'To request this exemption, you must provide all of the following documents:',
    bulletPoints: [
      'Manufacturer/engine paperwork showing pre-2000 engine build dates.',
      'ECM download linking engine serial number and build date to the vehicle VIN.',
      'Photo of engine serial number that matches the paperwork.',
    ],
  },
  addressForm: {
    title: 'Add Address Information',
    description:
      'To request this exemption, you must provide your primary reporting location address and city:',
  },
};

const uploadDocumentsForm = computed(
  () => formsLookup[selectedExemptReason.value?.id]
);

const isDocumentUploadEnabled = computed(() => {
  return store.state.users.featureFlags
    ?.display_upload_documents_in_exempt_eld_modal?.enabled;
});

const dropFiles = (event) => {
  const files = Array.from(event.dataTransfer.files);
  addFiles(files);
};

const onFilesUpload = (event) => {
  const files = Array.from(event.target.files);
  addFiles(files);
};

const addFiles = (files) => {
  files.forEach((file) => {
    if (!selectedFiles.value.find((f) => f.name === file.name)) {
      selectedFiles.value.push({
        file: file,
        name: file.name,
        uploading: false,
        uploaded: false,
        error: null,
      });
    }
  });
};

const removeFile = (index) => {
  selectedFiles.value.splice(index, 1);
};

const isUploadRequired = computed(
  () =>
    selectedExemptReason.value.id === 'driver_does_not_travel_over_150_miles' ||
    selectedExemptReason.value.id === 'vehicle_has_glider_kit'
);

const prepareExemptionData = () => {
  const exemptionData = { ...selectedExemptReason.value };
  exemptionData.id = `${exemptionData.id}`;

  // If files are required and selected, add them to FormData
  if (
    isDocumentUploadEnabled.value &&
    isUploadRequired.value &&
    selectedFiles.value &&
    selectedFiles.value.length > 0
  ) {
    const formData = new FormData();

    selectedFiles.value.forEach((file) => {
      formData.append('documents[]', file.file);
    });

    exemptionData.documents = formData;
  }

  return exemptionData;
};

const street = ref('');
const city = ref('');
const state = ref('');
const zip = ref('');

const address = computed(() => {
  return street.value
    .trim()
    .concat(' ')
    .concat(city.value.trim())
    .concat(' ')
    .concat(state.value.trim())
    .concat(' ')
    .concat(zip.value.trim());
});

const isFormFilled = computed(() => {
  return (
    street.value.length &&
    city.value.length &&
    state.value.length &&
    state.value !== 'none' &&
    zip.value.length
  );
});

const isSaveEldExemptButtonDisabled = computed(() => {
  if (showAddressForm.value) {
    return isFormFilled.value ? false : true;
  }
  if (
    !selectedExemptReason.value ||
    !isEldExemptAttestationCheckboxChecked.value
  ) {
    return true;
  }
  if (
    isDocumentUploadEnabled.value &&
    isUploadRequired.value &&
    !selectedFiles.value.length
  ) {
    return true;
  }
  return false;
});

const handleAssignDriverContinueClick = () => {
  phoneError.value = getPhoneError();
  if (phoneError.value) {
    return;
  }
  selectedDriver.value = {
    name: assignDriverNameInput.value,
    phone: assignDriverPhoneNumber.value,
  };
  goForward();
};

const showInputErrorBorders = ref(false);

// const handleChangeVehicleClick = () => {
//   // reset lists
//   filteredVehicles.value = allVehicles.value;
//   filteredRelayVehicles.value = relayVehicles.value;
//   if (props.workflowType === 'assignVehicle') {
//     currentStepIndex.value = currentStepIndex.value - 6;
//     return;
//   }
//   currentStepIndex.value = currentStepIndex.value - 2;
// };

const handleConfirmVehicleClick = () => {
  if (props.loadTicketId) {
    confirmVehicle();
  } else if (
    // If ELD exempt vehicle is assigned, prompt to assign driver, ask for relay etc
    selectedVehicle.value?.isEldExempt &&
    isAssignDriverMyLoadsFeatureEnabled.value
  ) {
    fetchDrivers();
    goForward();
    showInput.value = true;
  } else {
    assignVehicle();
  }
};

const confirmVehicle = async () => {
  if (!props.loadTicketId) {
    toast.open({
      message: 'Unable to confirm vehicle. Please try again.',
      type: 'error',
      position: 'top-right',
      duration: 2000,
    });
    return;
  }
  let res = null;
  try {
    res = await store.dispatch(
      'myLoadsStore/patchCloseAlert',
      props.loadTicketId
    );
  } catch (error) {
    toast.open({
      message: `Error: ${error.status == 403 ? 'Not Authorized to confirm vehicle' : 'Assigned vehicle not confirmed. Please try again.'}`,
      type: 'error',
      position: 'top-right',
      duration: 5000,
    });
    return;
  }
  if ([200, 201, 204].includes(res.status)) {
    emit('showRefreshButton');
    toast.open({
      message: 'Assigned vehicle successfully confirmed',
      position: 'top-right',
      // type: 'info',
      duration: 1500,
    });
    emit('closeModal');
  } else {
    toast.open({
      message: `Error: 'Assigned vehicle not confirmed. Please try again.`,
      type: 'error',
      position: 'top-right',
      duration: 5000,
    });
  }
};

const handleAcceptTermsClick = async () => {
  const id = props.rateConId;
  if (!id) {
    toast.open({
      message: 'Unable to accept rate confirmation. Please try again.',
      type: 'error',
      position: 'top-right',
      duration: 2000,
    });
    return;
  }
  const payload = {};
  if (selectedDriver.value?.phone) {
    payload.load_phone_number = {
      driver_phone_number: selectedDriver.value.phone,
      driver_name: selectedDriver.value.name,
    };
  }
  if (selectedVehicle.value?.id) {
    payload.carrier_vehicle_id = selectedVehicle.value.id;
  }
  if (assignTrailerNumberInput.value?.toString().trim().length) {
    payload.trailer_number = assignTrailerNumberInput.value.toString().trim();
  }
  if (selectedRelayVehicle.value?.id && pickupDateTimeUtcIsoString.value) {
    payload.relays = [
      {
        inbound_carrier_vehicle_id: selectedVehicle.value.id,
        outbound_carrier_vehicle_id: selectedRelayVehicle.value.id,
        switch_at: pickupDateTimeUtcIsoString.value,
      },
    ];
  }
  try {
    const res = await store.dispatch('rates/acceptRateTerms', {
      id: id,
      payload: payload,
    });

    if ([200, 201, 204].includes(res?.status)) {
      store.dispatch('rates/getRateDetail', parseInt(id));
      toast?.open({
        type: 'success',
        message: 'Terms accepted',
        position: 'top-right',
        duration: 5000,
      });
      emit('closeModal');
    }
  } catch (error) {
    console.log('error: ', error);
    toast?.open({
      type: 'error',
      message: 'An error occurred. Please try again.',
      position: 'top-right',
      duration: 5000,
    });
  }
};

const fetchDrivers = () => {
  store.dispatch('myLoadsStore/getDrivers');
};

const isDriversListLoading = computed(
  () => store.state.myLoadsStore.drivers.isLoading
);

const driversList = computed(() => {
  const list = [{ id: 'none', label: 'Select' }];
  store.state.myLoadsStore.drivers?.allDrivers.forEach((driver) => {
    list.push({
      id: driver.id,
      label: driver.first_name + ' ' + driver.last_name,
    });
  });
  return list;
});

watch(
  driversList,
  (newVal) => {
    if (newVal.length > 1) {
      isAddDriver.value = false;
    } else {
      isAddDriver.value = true;
    }
  },
  { deep: true }
);

const isAddDriver = ref(false);

const isEldExemptAssignDriverButtonText = computed(() => {
  return isAddDriver.value ? 'Invite' : 'Continue';
});

const eldExemptEmailError = ref('');

const eldExemptAssignDriverEmailInput = ref('');

const isEmailValid = ref(false);

const handleEldExemptAssignDriverEmailInputChange = (e) => {
  const value = e.target.value;
  eldExemptAssignDriverEmailInput.value = value.trim();
  if (value.length && !isEmail(value)) {
    isEmailValid.value = false;
  } else if (value.length && isEmail(value)) {
    eldExemptEmailError.value = '';
    isEmailValid.value = true;
  }
};

const getEmailError = () => {
  if (!isEmail(eldExemptAssignDriverEmailInput.value)) {
    console.log('getEmailError: email is not valid');
    return `Please enter a valid email address.`;
  }
  return '';
};

let eldExemptEmailTimeoutId = null;
watch(eldExemptAssignDriverEmailInput, (newVal) => {
  // clear exisiting errors
  eldExemptEmailError.value = '';
  if (newVal) {
    // if value is not empty, debounce for displaying email errors
    if (eldExemptEmailTimeoutId) {
      clearTimeout(eldExemptEmailTimeoutId);
    }
    eldExemptEmailTimeoutId = setTimeout(() => {
      eldExemptEmailError.value = getEmailError();
    }, 3000);
  }
});

const handleEldExemptAssignDriverEmailInputBlur = () => {
  if (eldExemptEmailTimeoutId) {
    clearTimeout(eldExemptEmailTimeoutId);
  }
  eldExemptEmailError.value = getEmailError();
};

const isEldExemptAssignDriverButtonDisabled = computed(() => {
  if (isAddDriver.value) {
    if (
      eldExemptAssignDriverFirstNameInput.value === '' ||
      eldExemptAssignDriverLastNameInput.value === '' ||
      eldExemptAssignDriverEmailInput.value === '' ||
      eldExemptAssignDriverPhoneInput.value === '' ||
      eldExemptPhoneError.value ||
      eldExemptEmailError.value
    ) {
      return true;
    } else {
      return false;
    }
  } else {
    if (
      eldExemptSelectedDriver.value === 'none' ||
      !eldExemptSelectedDriver.value?.id
    ) {
      return true;
    } else {
      return false;
    }
  }
});

const handleEldExemptAssignDriverContinueClick = async () => {
  if (!isAddDriver.value) {
    skipEldExemptStep();
    return;
  }
  const payload = {
    phone_e164: eldExemptAssignDriverPhoneNumber.value,
    email: eldExemptAssignDriverEmailInput.value,
    scopes: ['driver'],
    site: 'highway.com',
  };
  try {
    const res = await store.dispatch(
      'userInvitations/createUserInvitation',
      payload
    );
    if (res.data.id) {
      // show pending account verfication prompt
      currentStepIndex.value = workflow.value.steps.length - 1;
      toast?.open({
        type: 'success',
        message: 'Invitation sent',
        position: 'top-right',
        duration: 5000,
      });
      const inviteResponse = await store.dispatch(
        'myLoadsStore/addDriverInvitationToLoad',
        {
          id: props.loadId,
          payload: {
            driver_carrier_vehicle_id: selectedVehicle.value.id,
            driver_user_invitation_id: res.data.id,
          },
        }
      );
      if (inviteResponse.data.message === 'User invitation assigned') {
        toast.open({
          message: inviteResponse.data.message,
          type: 'success',
          position: 'top-right',
          duration: 1500,
        });
      } else {
        toast.open({
          message: inviteResponse.data.message,
          type: 'error',
          position: 'top-right',
          duration: 5000,
        });
      }
    } else {
      if (res.data.email.length) {
        eldExemptEmailError.value = `Email address already exists.`;
      } else {
        throw new Error('Failed to create user invitation');
      }
    }
  } catch (error) {
    console.log('error: ', error);
    toast?.open({
      type: 'error',
      message: 'An error occurred. Please try again.',
      position: 'top-right',
      duration: 5000,
    });
  }
};

const steps = computed(() => [
  {
    step: 1,
    id: 'assignDriver',
    title: `Driver's Information`,
    type: 'form',
    description:
      'Enter the driver assigned to the load for this rate confirmation.',
    primaryButton: {
      text: 'Continue',
      type: 'submit',
      isDisabled: isAssignDriverButtonDisabled.value,
      style: 'primary',
      action: handleAssignDriverContinueClick,
    },
    secondaryButton: {
      text: 'Cancel',
      type: 'button',
      style: 'subtle-button',
      action: () => emit('closeModal'),
    },
  },
  {
    step: 2,
    id: 'assignVehicle',
    title: 'Assign Vehicle',
    type: 'form',
    input: assignVehicleInput.value,
    inputChange: handleAssignVehicleInputChange,
    enterPress: handleAssignVehicleEnterPress,
    radioButtonClick: (vehicle) => handleAssignVehicleRadioButtonClick(vehicle),
    vehiclesList: filteredVehicles.value,
    description: 'Select a vehicle to assign to this load.',
    primaryButton: {
      text: 'Continue',
      type: 'submit',
      isDisabled: isAssignVehicleButtonDisabled.value,
      action: handleAssignVehicleContinueClick,
    },
    secondaryButton: {
      text: 'Cancel',
      type: 'button',
      style: 'subtle-button',
      action: () => emit('closeModal'),
    },
  },
  {
    step: 3,
    id: 'pickupVehicleNoEld',
    vehicle: selectedVehicle.value,
    type: 'form',
    group: 'eldExempt',
    title: 'No Active ELD Connection',
    description:
      'Please check the following steps below or mark the vehicle as ELD exempt.',
    checkList: [
      'Confirm the correct ELD account is connected to Highway.',
      'Ensure the VIN is correctly entered in the ELD account.',
      'Check that the ELD device in the truck is working and active.',
      'Verify that the correct permissions are set up in the ELD account.',
      'Notify your insurance agency if the truck is no longer in service so they can update your VIN schedule and COI.',
    ],
    primaryButton: {
      text: 'Change Vehicle',
      type: 'submit',
      action: goBack,
    },
    secondaryButton: {
      text: 'Mark as ELD Exempt',
      type: 'button',
      style: 'default-button',
      action: goForward,
    },
  },
  {
    step: 4,
    id: 'pickupVehicleClaimExemption',
    vehicle: selectedVehicle.value,
    type: 'form',
    group: 'eldExempt',
    title: 'Claim ELD Exemption',
    description: `An ELD exemption is a legal declaration under 80 FR 78292, 49 CFR §395.1. False, misleading, or denied claims may impact your eligibility to service our Customer's contracts. Select a valid exemption reason below and Highway will verify your claim.`,
    dropdownList: [
      { id: 'none', label: 'Select' },
      { id: 'vehicle_has_glider_kit', label: 'Glider Kit' },
      {
        id: 'driver_does_not_travel_over_150_miles',
        label: 'Less Than 150 Miles',
      },
    ],
    addressForm: {
      title: 'Add Address Information',
      description:
        'To request this exemption, you must provide your primary reporting location address and city:',
    },
    checkboxLabel: 'I attest that this eld exemption is lawful and accurate.',
    checkbox: isEldExemptAttestationCheckboxChecked.value,
    checkboxClick: () => {
      isEldExemptAttestationCheckboxChecked.value =
        !isEldExemptAttestationCheckboxChecked.value;
    },
    primaryButton: {
      text: 'Save',
      type: 'submit',
      isDisabled: isSaveEldExemptButtonDisabled.value,
      action: handleEldExemptTypeSaveClick,
    },
    secondaryButton: {
      text: 'Cancel',
      type: 'button',
      style: 'subtle-button',
      action: goBack,
    },
  },
  {
    step: 5,
    vehicle: selectedVehicle.value,
    id: 'pickupVehiclePendingVerification',
    type: 'prompt',
    group: 'eldExempt',
    title: 'Pending Verification',
    descriptionHtml:
      '<p>The Highway team will review the exemption request in 1-3 days. Upon approval, the vehicle will be eligible to assign to loads. For any further questions, contact <a class="text-yellow-700 underline hover:text-yellow-800 active:text-yellow-900 tracking-tight"" href="mailto:eldservice@highway.com">eldservice@highway.com</a>.</p>',
    primaryButton: {
      text: 'Done',
      type: 'button',
      action: () => emit('closeModal'),
    },
  },
  {
    step: 6,
    id: 'isRelayPrompt',
    title: 'Is this a relay load?',
    type: 'prompt',
    descriptionHtml:
      '<p>Do you want to assign this as a relay load? A relay means another vehicle will take over part of the trip.</p>',
    primaryButton: {
      text: 'Yes',
      type: 'button',
      action: () => {
        currentStepIndex.value = currentStepIndex.value + 2;
      },
    },
    secondaryButton: {
      text: isRelayPromptSecondaryButtonText.value,
      type: 'button',
      style: 'secondary-button',
      action: handleAssignPickupClick,
    },
  },
  {
    step: 7,
    id: 'isExtraRelayPrompt',
    title: 'Using an ELD Exempt Vehicle?',
    type: 'prompt',
    descriptionHtml:
      '<p>This load is over a 150 miles. Will a second vehicle be assigned to deliver it?</p>',
    primaryButton: {
      text: 'Yes',
      type: 'button',
      action: goForward,
    },
    secondaryButton: {
      text: 'No, Assign',
      type: 'button',
      style: 'secondary-button',
      action: assignVehicle,
    },
  },
  {
    step: 8,
    id: 'assignRelayVehicle',
    type: 'form',
    input: assignRelayVehicleInput.value,
    inputChange: handleAssignRelayVehicleInputChange,
    enterPress: handleAssignRelayVehicleEnterPress,
    radioButtonClick: (vehicle) =>
      handleAssignRelayVehicleRadioButtonClick(vehicle),
    vehiclesList: filteredRelayVehicles.value,
    title: 'Assign Relay Vehicle',
    description: 'Select the vehicle that will take over this load.',
    primaryButton: {
      text: 'Continue',
      type: 'submit',
      isDisabled: isAssignRelayVehicleButtonDisabled.value,
      action: handleAssignRelayVehicleContinueClick,
    },
    secondaryButton: {
      text: 'Cancel',
      type: 'button',
      style: 'subtle-button',
      action: () => {
        currentStepIndex.value = currentStepIndex.value - 2;
      },
    },
  },
  {
    step: 9,
    id: 'relayVehicleNoEld',
    vehicle: selectedRelayVehicle.value,
    type: 'form',
    group: 'eldExempt',
    title: 'No Active ELD Connection',
    description:
      'Please check the following steps below or mark the vehicle as ELD exempt.',
    checkList: [
      'Confirm the correct ELD account is connected to Highway.',
      'Ensure the VIN is correctly entered in the ELD account.',
      'Check that the ELD device in the truck is working and active.',
      'Verify that the correct permissions are set up in the ELD account.',
      'Notify your insurance agency if the truck is no longer in service so they can update your VIN schedule and COI.',
    ],
    primaryButton: {
      text: 'Change Vehicle',
      type: 'submit',
      action: goBack,
    },
    secondaryButton: {
      text: 'Mark as ELD Exempt',
      type: 'button',
      style: 'default-button',
      action: goForward,
    },
  },
  {
    step: 10,
    id: 'relayVehicleClaimExemption',
    vehicle: selectedRelayVehicle,
    type: 'form',
    group: 'eldExempt',
    title: 'Claim ELD Exemption',
    description: `An ELD exemption is a legal declaration under 80 FR 78292, 49 CFR §395.1. False, misleading, or denied claims may impact your eligibility to service our Customer's contracts. Select a valid exemption reason below and Highway will verify your claim.`,
    dropdownList: [
      { id: 'none', label: 'Select' },
      { id: 'vehicle_has_glider_kit', label: 'Glider Kit' },
      {
        id: 'driver_does_not_travel_over_150_miles',
        label: 'Less Than 150 Miles',
      },
    ],
    checkboxLabel: 'I attest that this eld exemption is lawful and accurate.',
    checkbox: isEldExemptAttestationCheckboxChecked.value,
    checkboxClick: () => {
      isEldExemptAttestationCheckboxChecked.value =
        !isEldExemptAttestationCheckboxChecked.value;
    },
    primaryButton: {
      text: 'Save',
      type: 'submit',
      isDisabled: isSaveEldExemptButtonDisabled.value,
      action: handleEldExemptTypeSaveClick,
    },
    secondaryButton: {
      text: 'Cancel',
      type: 'button',
      style: 'subtle-button',
      action: goBack,
    },
  },
  {
    step: 11,
    id: 'relayVehiclePendingVerification',
    vehicle: selectedRelayVehicle,
    type: 'prompt',
    group: 'eldExempt',
    title: 'Pending Verification',
    descriptionHtml:
      '<p>The Highway team will review the exemption request in 1-3 days. Upon approval, the vehicle will be eligible to assign to loads. For any further questions, contact <a class="text-yellow-700 underline hover:text-yellow-800 active:text-yellow-900 tracking-tight"" href="mailto:eldservice@highway.com">eldservice@highway.com</a>.</p>',
    primaryButton: {
      text: 'Done',
      type: 'button',
      action: () => emit('closeModal'),
    },
  },
  {
    step: 12,
    id: 'relayPickupTime',
    type: 'form',
    title: 'Enter Relay Pickup Time',
    description: 'Provide the pickup date for the relay.',
    primaryButton: {
      text: relayPickupTimePrimaryButtonText.value,
      type: 'submit',
      action: handleRelayPickupTimeContinueClick,
    },
    secondaryButton: {
      text: 'Cancel',
      type: 'button',
      style: 'subtle-button',
      action: () => {
        currentStepIndex.value = currentStepIndex.value - 4;
      },
    },
  },
  {
    step: 13,
    id: 'assignTrailer',
    type: 'form',
    title: 'Trailer Number',
    description:
      '(Optional) Enter the trailer number for reference. You can skip this step if not available.',
    primaryButton: {
      text: 'Continue',
      type: 'submit',
      action: goForward,
    },
    secondaryButton: {
      text: 'Cancel',
      type: 'button',
      style: 'subtle-button',
      action: handleAssignTrailerCancelClick,
    },
  },
  {
    step: 14,
    id: 'rateConfirmationTerms',
    type: 'form',
    title: 'Rate Confirmation Terms',
    description:
      'Please review and accept the following terms and conditions as specified by Highway before reviewing this rate confirmation.',
    disclaimer: props.rateTerms,
    checkboxLabel: 'I accept the terms and conditions listed above.',
    checkbox: isTermsAndConditionsCheckboxChecked.value,
    checkboxClick: () => {
      isTermsAndConditionsCheckboxChecked.value =
        !isTermsAndConditionsCheckboxChecked.value;
    },
    primaryButton: {
      text: 'Accept',
      type: 'submit',
      isDisabled: isAcceptTermsButtonDisabled.value,
      action: handleAcceptTermsClick,
    },
    secondaryButton: {
      text: 'Cancel',
      type: 'button',
      style: 'subtle-button',
      action: goBack,
    },
  },
  {
    step: 15,
    id: 'equipmentEldExemptPrompt',
    type: 'form',
    vehicle: selectedVehicle.value,
    group: 'eldExempt',
    title: 'No Active ELD Connection',
    description:
      'Please check the following steps below or mark the vehicle as ELD exempt.',
    checkList: [
      'Confirm the correct ELD account is connected to Highway.',
      'Ensure the VIN is correctly entered in the ELD account.',
      'Check that the ELD device in the truck is working and active.',
      'Verify that the correct permissions are set up in the ELD account.',
      'Notify your insurance agency if the truck is no longer in service so they can update your VIN schedule and COI.',
    ],
    primaryButton: {
      text: 'Done',
      type: 'submit',
      action: () => emit('closeModal'),
    },
    secondaryButton: {
      text: 'Mark as ELD Exempt',
      type: 'button',
      style: 'secondary-button',
      action: goForward,
    },
  },
  {
    step: 16,
    id: 'equipmentClaimExemption',
    type: 'form',
    group: 'eldExempt',
    title: 'Claim ELD Exemption',
    vehicle: selectedVehicle.value,
    description: `An ELD exemption is a legal declaration under 80 FR 78292, 49 CFR §395.1. False, misleading, or denied claims may impact your eligibility to service our Customer's contracts. Select a valid exemption reason below and Highway will verify your claim.`,
    dropdownList: [
      { id: 'none', label: 'Select' },
      { id: 'vehicle_has_glider_kit', label: 'Glider Kit' },
      {
        id: 'driver_does_not_travel_over_150_miles',
        label: 'Less Than 150 Miles',
      },
    ],
    checkboxLabel: 'I attest that this eld exemption is lawful and accurate.',
    checkbox: isEldExemptAttestationCheckboxChecked.value,
    checkboxClick: () => {
      isEldExemptAttestationCheckboxChecked.value =
        !isEldExemptAttestationCheckboxChecked.value;
    },
    primaryButton: {
      text: 'Save',
      type: 'submit',
      isDisabled: isSaveEldExemptButtonDisabled.value,
      action: handleEldExemptTypeSaveClick,
    },
    secondaryButton: {
      text: 'Cancel',
      type: 'button',
      style: 'subtle-button',
      action: () => emit('closeModal'),
    },
  },
  {
    step: 17,
    id: 'equipmentEldExemptPendingNotice',
    type: 'prompt',
    vehicle: selectedVehicle.value,
    group: 'eldExemptPendingNotice',
    title: 'Pending Verification',
    descriptionHtml:
      '<p>The Highway team will review the exemption request in 1-3 days. Upon approval, the vehicle will be eligible to assign to loads. For any further questions, contact <a class="text-yellow-700 underline hover:text-yellow-800 active:text-yellow-900 tracking-tight"" href="mailto:eldservice@highway.com">eldservice@highway.com</a>.</p>',
    primaryButton: {
      text: 'Done',
      type: 'submit',
      action: () => emit('closeModal'),
    },
  },
  {
    step: 18,
    id: 'eldExemptAssignDriver',
    type: 'form',
    vehicle: selectedVehicle.value,
    title: 'Assign Driver',
    inputChange: handleAssignVehicleInputChange,
    enterPress: handleAssignVehicleEnterPress,
    radioButtonClick: (vehicle) => handleAssignVehicleRadioButtonClick(vehicle),
    description: 'Select a driver to assign to this load.',
    primaryButton: {
      text: isEldExemptAssignDriverButtonText.value,
      type: 'submit',
      isDisabled: isEldExemptAssignDriverButtonDisabled.value,
      action: handleEldExemptAssignDriverContinueClick,
    },
    secondaryButton: {
      text: 'Cancel',
      type: 'button',
      style: 'subtle-button',
      action: goBack,
    },
  },
  {
    step: 19,
    id: 'assignDriverPendingVerification',
    type: 'prompt',
    group: 'eldExempt',
    title: 'Pending Verification',
    descriptionHtml:
      '<p>The driver will receive an invitation via email to create a Highway account. Once the account has been created and verified by Highway, the driver and the ELD exempt vehicle will be assigned to the load.</p>',
    primaryButton: {
      text: 'Done',
      type: 'button',
      action: () => emit('closeModal'),
    },
  },
]);

const rateConSteps = computed(() => {
  const steps = [];
  if (isAssignDriverRateConFeatureEnabled.value) {
    steps.push(1);
  }
  if (isAssignVehicleRateConFeatureEnabled.value) {
    steps.push(2, 6, 7, 8, 12, 13);
  }
  steps.push(14);
  return steps;
});

const workflowsLookup = computed(() => ({
  rateCon: {
    steps: rateConSteps.value,
    showLoadDetails: true,
    showDriverDetails: true,
    showVehicleDetails: isAssignVehicleRateConFeatureEnabled.value
      ? true
      : false,
    showStepHeader: true,
  },
  assignVehicle: {
    steps: [2, 18, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 19],
    showLoadDetails: true,
    showDriverDetails: true,
    showVehicleDetails: true,
    showStepHeader: false,
  },
  eldExempt: {
    steps: [15, 16, 17],
    showLoadDetails: false,
    showDriverDetails: false,
    showVehicleDetails: true,
    showStepHeader: false,
  },
}));

const workflow = computed(() => workflowsLookup.value[props.workflowType]);

const currentStep = computed(
  () => steps.value[workflow.value.steps[currentStepIndex.value] - 1]
);

const showPendingVerificationIcon = computed(
  () => currentStep.value?.title === 'Pending Verification'
);
</script>

<template>
  <div
    ref="modalRef"
    class="flex min-h-screen w-screen flex-1 items-center justify-center p-4"
  >
    <div
      class="flex w-full max-w-fit flex-1 flex-col gap-6 rounded-xl bg-white p-6 shadow-[0px_0px_1px_0px_rgba(31,36,46,0.31),0px_8px_12px_0px_rgba(31,36,46,0.16)]"
    >
      <!-- prompt -->
      <div v-if="currentStep?.type === 'prompt'">
        <div class="flex max-w-[300px] flex-col items-center justify-start">
          <div class="flex w-full justify-end">
            <MyLoadsDesktopButtonComponent
              button-class="subtle-button rounded-[5px]"
              button-type="button"
              @button-click="$emit('closeModal')"
            >
              <template #leftIcon>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M18.3639 7.05044L16.9497 5.63623L11.9999 10.586L7.0502 5.63623L5.63599 7.05044L10.5857 12.0002L5.63599 16.9499L7.0502 18.3642L11.9999 13.4144L16.9497 18.3642L18.3639 16.9499L13.4141 12.0002L18.3639 7.05044Z"
                    fill="#6C7C9D"
                  />
                </svg>
              </template>
            </MyLoadsDesktopButtonComponent>
          </div>
          <div
            class="flex flex-col items-center justify-start gap-4 self-stretch"
          >
            <img
              v-if="showPendingVerificationIcon"
              width="59"
              src="@/assets/pending-icon.svg"
              alt="Pending verification icon"
            />
            <img
              v-if="currentStep?.id === 'isExtraRelayPrompt'"
              width="60"
              src="@/assets/warning-icon.svg"
            />
            <header
              class="justify-start font-['Poppins'] text-lg font-medium leading-7 text-zinc-700"
            >
              {{ currentStep?.title }}
            </header>
            <div
              v-if="currentStep?.descriptionHtml"
              class="justify-start self-stretch text-center text-sm font-normal leading-tight tracking-tight text-steel-700"
              v-html="currentStep?.descriptionHtml"
            ></div>
            <div
              v-if="!isDesktop"
              class="flex items-center justify-between gap-2 self-stretch"
            >
              <MyLoadsDesktopButtonComponent
                v-if="currentStep?.secondaryButton"
                :button-class="`flex-1 ${currentStep?.secondaryButton.style} rounded-[3px] px-3 py-1.5`"
                :button-text="currentStep?.secondaryButton.text"
                button-type="button"
                text-class="text-sm font-medium leading-10 tracking-tight"
                @button-click="currentStep?.secondaryButton.action"
              />
              <MyLoadsDesktopButtonComponent
                v-if="currentStep?.primaryButton"
                button-class="flex-1 primary-button rounded-[3px] px-3 py-1.5"
                :button-text="currentStep?.primaryButton.text"
                button-type="button"
                text-class="text-sm font-medium leading-10 tracking-tight"
                @button-click="currentStep?.primaryButton.action"
              />
            </div>
            <div
              v-else
              class="flex items-center justify-end gap-2 self-stretch"
            >
              <MyLoadsDesktopButtonComponent
                v-if="currentStep?.secondaryButton"
                :button-class="`${currentStep?.secondaryButton.style} rounded-[5px]  px-3 py-1 h-8`"
                text-class="font-medium text-sm leading-10 tracking-tight"
                button-type="button"
                :button-text="currentStep?.secondaryButton.text"
                @button-click="currentStep?.secondaryButton.action"
              />
              <MyLoadsDesktopButtonComponent
                v-if="currentStep?.primaryButton"
                button-class="primary-button rounded-[5px] px-3 py-1 h-8"
                button-type="button"
                text-class="font-medium text-sm leading-10 tracking-tight"
                :button-text="currentStep?.primaryButton.text"
                @button-click="currentStep?.primaryButton.action"
              />
            </div>
          </div>
        </div>
      </div>
      <!-- form -->
      <div v-else>
        <div class="flex w-full flex-col gap-4">
          <div class="flex w-full items-center justify-between gap-4">
            <div class="flex gap-2">
              <header
                class="flex-1 justify-start text-xl font-medium leading-7 text-steel-1100"
              >
                {{ currentStep?.title }}
              </header>
            </div>
            <MyLoadsDesktopButtonComponent
              button-class="subtle-button rounded-[5px]"
              button-type="button"
              @button-click="$emit('closeModal')"
            >
              <template #leftIcon>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M18.3639 7.05044L16.9497 5.63623L11.9999 10.586L7.0502 5.63623L5.63599 7.05044L10.5857 12.0002L5.63599 16.9499L7.0502 18.3642L11.9999 13.4144L16.9497 18.3642L18.3639 16.9499L13.4141 12.0002L18.3639 7.05044Z"
                    fill="#6C7C9D"
                  />
                </svg>
              </template>
            </MyLoadsDesktopButtonComponent>
          </div>
          <p
            v-if="
              currentStep?.id === 'assignVehicle' &&
              !showInput &&
              !assignedVehicle?.id &&
              !selectedVehicle?.id &&
              pendingVehicle?.id
            "
            class="flex w-full justify-center rounded-md bg-orange-100 px-2 py-1 text-sm font-normal text-orange-700"
          >
            <span>Status: Waiting for driver to activate Highway account.</span>
          </p>
          <p
            v-else
            class="w-full max-w-[475px] text-sm font-normal text-steel-700"
          >
            {{ currentStep?.description }}
          </p>
          <div class="flex max-w-[475px] flex-wrap gap-2">
            <div
              v-if="
                workflow.showLoadDetails && currentStep?.group !== 'eldExempt'
              "
              class="flex w-[230px] gap-1 rounded-[5px] bg-steel-100 p-3"
            >
              <div class="flex w-fit flex-col flex-nowrap justify-start">
                <div
                  class="flex h-5 flex-nowrap items-center text-xs font-normal leading-tight text-steel-700"
                >
                  Load ID
                </div>
                <div
                  v-if="origin?.appointmentDay"
                  class="flex h-5 flex-nowrap items-center text-xs font-normal leading-tight text-steel-700"
                >
                  Pickup Date
                </div>
                <div
                  v-if="origin?.city || origin?.state"
                  class="flex h-5 flex-nowrap items-center text-xs font-normal leading-tight text-steel-700"
                >
                  Origin
                </div>
                <div
                  v-if="destination?.city || destination?.state"
                  class="flex h-5 flex-nowrap items-center text-xs font-normal leading-tight text-steel-700"
                >
                  Destination
                </div>
              </div>
              <div></div>
              <div
                class="flex min-w-fit flex-1 flex-col items-end justify-start"
              >
                <div
                  class="flex h-5 flex-nowrap items-center text-xs font-normal capitalize leading-tight text-steel-900"
                >
                  {{ externalLoadId || '-' }}
                </div>
                <div
                  v-if="origin?.appointmentDay"
                  class="flex h-5 flex-nowrap items-center text-xs font-normal capitalize leading-tight text-steel-900"
                  :title="`${origin?.appointmentDay}${origin?.appointmentTime ? ` • ${origin?.appointmentTime}` : ''}`"
                >
                  {{
                    `${origin?.appointmentDay}${origin?.appointmentTime ? ` • ${origin?.appointmentTime}` : ''}`
                  }}
                </div>
                <div
                  v-if="origin?.city || origin?.state"
                  class="flex h-5 items-center justify-end truncate text-xs font-normal capitalize leading-tight text-steel-900"
                  :title="`${origin?.city}${origin?.city === '-' ? '' : `, ${origin?.state}`}`"
                >
                  {{
                    `${origin?.city.slice(0, 12)}${origin?.city.length > 12 ? '...' : ''}${origin?.city === '-' ? '' : `, ${origin?.state}`}`
                  }}
                </div>
                <div
                  v-if="destination?.city || destination?.state"
                  class="flex h-5 min-w-fit flex-nowrap items-center text-xs font-normal capitalize leading-tight text-steel-900"
                >
                  {{
                    `${destination?.city.slice(0, 12)}${destination?.city.length > 12 ? '...' : ''}${destination?.city === '-' ? '' : `, ${destination?.state}`}`
                  }}
                </div>
              </div>
            </div>
            <div
              v-if="
                currentStep?.id !== 'assignDriver' &&
                (workflow.showDriverDetails || workflow.showVehicleDetails) &&
                currentStep?.group !== 'eldExempt'
              "
              class="flex w-[230px] gap-1 rounded-[5px] bg-steel-100 p-3"
            >
              <div class="flex min-w-fit flex-1 flex-col justify-center">
                <div
                  v-if="workflow.showDriverDetails"
                  class="flex h-5 items-center text-xs font-normal leading-tight text-steel-700"
                >
                  Driver Name
                </div>
                <div
                  v-if="workflow.showDriverDetails"
                  class="flex h-5 items-center text-xs font-normal leading-tight text-steel-700"
                >
                  Driver Phone
                </div>
                <div
                  class="flex h-5 items-center text-xs font-normal leading-tight text-steel-700"
                >
                  Truck #
                </div>
                <div
                  class="flex h-5 items-center text-xs font-normal leading-tight text-steel-700"
                >
                  VIN
                </div>
                <div
                  v-if="props.workflowType === 'rateCon'"
                  class="flex h-5 items-center text-xs font-normal leading-tight text-steel-700"
                >
                  Trailer #
                </div>
              </div>
              <div></div>
              <div
                class="flex min-w-fit flex-1 flex-col items-end justify-center"
              >
                <div
                  v-if="workflow.showDriverDetails"
                  class="flex h-5 items-center text-xs font-normal capitalize leading-tight text-steel-900"
                  :title="selectedDriver?.name"
                >
                  {{
                    selectedDriver?.name
                      ? selectedDriver.name.slice(0, 18) +
                        (selectedDriver.name.length > 18 ? '...' : '')
                      : '-'
                  }}
                </div>
                <div
                  v-if="workflow.showDriverDetails"
                  class="flex h-5 items-center text-xs font-normal capitalize leading-tight text-steel-900"
                >
                  {{ selectedDriver?.phone || '-' }}
                </div>
                <div
                  class="flex h-5 items-center text-xs font-normal capitalize leading-tight text-steel-900"
                  :class="{
                    'text-orange-700':
                      !selectedVehicle?.id && pendingVehicle?.id,
                  }"
                  :title="
                    selectedVehicle?.truck_number ||
                    pendingVehicle?.truck_number ||
                    '-'
                  "
                >
                  {{
                    selectedVehicle?.truck_number
                      ? selectedVehicle?.truck_number.slice(0, 18) +
                        (selectedVehicle?.truck_number.length > 18 ? '...' : '')
                      : pendingVehicle?.truck_number
                        ? pendingVehicle?.truck_number.slice(0, 18) +
                          (pendingVehicle?.truck_number.length > 18
                            ? '...'
                            : '')
                        : '-'
                  }}
                </div>
                <div
                  class="flex h-5 items-center text-xs font-normal capitalize leading-tight text-steel-900"
                  :class="{
                    'text-orange-700':
                      !selectedVehicle?.id && pendingVehicle?.id,
                  }"
                  :title="selectedVehicle?.vin || pendingVehicle?.vin || '-'"
                >
                  {{ selectedVehicle?.vin || pendingVehicle?.vin || '-' }}
                </div>
                <div
                  v-if="props.workflowType === 'rateCon'"
                  class="flex h-5 items-center text-xs font-normal capitalize leading-tight text-steel-900"
                  :title="assignTrailerNumberInput || '-'"
                >
                  {{ assignTrailerNumberInput || '-' }}
                </div>
              </div>
            </div>
            <div
              v-if="
                currentStep?.group !== 'eldExempt' && selectedRelayVehicle?.id
              "
              class="flex w-[230px] gap-1 rounded-[5px] bg-steel-100 p-3"
            >
              <div class="flex min-w-fit flex-1 flex-col justify-center">
                <div
                  class="flex h-5 items-center text-xs font-normal leading-tight text-steel-700"
                >
                  Relay Truck #
                </div>
                <div
                  class="flex h-5 items-center text-xs font-normal leading-tight text-steel-700"
                >
                  Relay VIN
                </div>
              </div>
              <div></div>
              <div
                class="flex min-w-fit flex-1 flex-col items-end justify-center"
              >
                <div
                  class="flex h-5 items-center text-xs font-normal capitalize leading-tight text-steel-900"
                  :title="selectedRelayVehicle?.truck_number"
                >
                  {{
                    selectedRelayVehicle?.truck_number
                      ? selectedRelayVehicle?.truck_number.slice(0, 18) +
                        (selectedRelayVehicle?.truck_number.length > 18
                          ? '...'
                          : '')
                      : '-'
                  }}
                </div>
                <div
                  class="flex h-5 items-center text-xs font-normal capitalize leading-tight text-steel-900"
                  :title="selectedRelayVehicle?.vin || '-'"
                >
                  {{ selectedRelayVehicle?.vin || '-' }}
                </div>
              </div>
            </div>
            <div
              v-if="currentStep?.group === 'eldExempt'"
              class="flex w-[230px] gap-1 rounded-[5px] bg-steel-100 p-3"
            >
              <div class="flex min-w-fit flex-1 flex-col justify-center">
                <div
                  class="flex h-5 items-center text-xs font-normal leading-tight text-steel-700"
                >
                  Truck #
                </div>
                <div
                  class="flex h-5 items-center text-xs font-normal leading-tight text-steel-700"
                >
                  VIN
                </div>
              </div>
              <div></div>
              <div
                class="flex min-w-fit flex-1 flex-col items-end justify-center"
              >
                <div
                  class="flex h-5 items-center text-xs font-normal capitalize leading-tight text-steel-900"
                  :title="selectedExemptVehicle?.truck_number"
                >
                  {{
                    selectedExemptVehicle?.truck_number
                      ? selectedExemptVehicle?.truck_number.slice(0, 18) +
                        (selectedExemptVehicle?.truck_number.length > 18
                          ? '...'
                          : '')
                      : '-'
                  }}
                </div>
                <div
                  class="flex h-5 items-center text-xs font-normal capitalize leading-tight text-steel-900"
                  :title="selectedExemptVehicle?.vin || '-'"
                >
                  {{ selectedExemptVehicle?.vin || '-' }}
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="
              !isDesktop &&
              assignedVehicle?.id &&
              currentStep?.id === 'assignVehicle' &&
              !showInput
            "
            class="flex min-w-fit items-center justify-between gap-2 self-stretch"
          >
            <MyLoadsDesktopButtonComponent
              button-class="flex-1 default-button min-w-fit rounded-[3px] px-3 py-1.5"
              button-text="Change Vehicle"
              text-class=" text-sm font-medium leading-10 tracking-tight"
              @button-click="showInput = true"
            />
            <MyLoadsDesktopButtonComponent
              button-class="flex-1 primary-button min-w-fit rounded-[3px] px-3 py-1.5"
              button-text="Confirm Vehicle"
              text-class="text-sm font-medium leading-10 tracking-tight"
              @button-click="handleConfirmVehicleClick"
            />
          </div>
          <div
            v-else-if="
              assignedVehicle?.id &&
              currentStep?.id === 'assignVehicle' &&
              !showInput
            "
            class="flex items-center justify-end gap-2 self-stretch"
          >
            <MyLoadsDesktopButtonComponent
              button-class="default-button rounded-[5px] px-3 py-1 h-8"
              text-class="font-medium text-sm leading-10 tracking-tight"
              button-text="Change Vehicle"
              @button-click="showInput = true"
            />
            <MyLoadsDesktopButtonComponent
              button-class="primary-button rounded-[5px] px-3 py-1 h-8"
              text-class="font-medium text-sm leading-10 tracking-tight"
              button-text="Confirm Vehicle"
              @button-click="handleConfirmVehicleClick"
            />
          </div>
          <div
            v-if="
              !isDesktop &&
              !assignedVehicle?.id &&
              pendingVehicle?.id &&
              currentStep?.id === 'assignVehicle' &&
              !showInput
            "
            class="flex min-w-fit items-center justify-between gap-2 self-stretch"
          >
            <MyLoadsDesktopButtonComponent
              button-class="flex-1 default-button min-w-fit rounded-[3px] px-3 py-1.5"
              button-text="Change Vehicle"
              text-class=" text-sm font-medium leading-10 tracking-tight"
              @button-click="showInput = true"
            />
            <MyLoadsDesktopButtonComponent
              button-class="flex-1 primary-button min-w-fit rounded-[3px] px-3 py-1.5"
              button-text="Done"
              text-class="text-sm font-medium leading-10 tracking-tight"
              @button-click="emit('closeModal')"
            />
          </div>
          <div
            v-else-if="
              !assignedVehicle?.id &&
              pendingVehicle?.id &&
              currentStep?.id === 'assignVehicle' &&
              !showInput
            "
            class="flex items-center justify-end gap-2 self-stretch"
          >
            <MyLoadsDesktopButtonComponent
              button-class="default-button rounded-[5px] px-3 py-1 h-8"
              text-class="font-medium text-sm leading-10 tracking-tight"
              button-text="Change Vehicle"
              @button-click="showInput = true"
            />
            <MyLoadsDesktopButtonComponent
              button-class="primary-button rounded-[5px] px-3 py-1 h-8"
              text-class="font-medium text-sm leading-10 tracking-tight"
              button-text="Done"
              @button-click="emit('closeModal')"
            />
          </div>
          <div
            v-if="currentStep?.title === 'No Active ELD Connection'"
            class="flex w-full max-w-[475px] flex-col items-start justify-center gap-4"
          >
            <div
              v-for="(item, index) in currentStep?.checkList"
              :key="item"
              class="flex w-full flex-col items-center justify-start gap-4"
            >
              <div class="flex w-full items-center gap-2">
                <svg
                  class="flex-shrink-0"
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                >
                  <path
                    d="M7.00024 0.333344C8.97816 0.333438 10.754 1.19566 11.9749 2.56381V2.56479C11.8988 2.5895 11.8323 2.6556 11.7004 2.78745L7.04419 7.44467C6.91218 7.57668 6.84588 7.6426 6.76978 7.66733C6.70286 7.68906 6.63064 7.68906 6.56372 7.66733C6.48761 7.6426 6.42131 7.57667 6.28931 7.44467L4.63306 5.78745C4.50121 5.6556 4.43471 5.5895 4.35864 5.56479C4.29181 5.54317 4.2194 5.54308 4.15259 5.56479C4.07665 5.58956 4.01077 5.65582 3.87915 5.78745L3.45435 6.21225C3.32247 6.34413 3.25641 6.41059 3.23169 6.48666C3.21008 6.55349 3.20998 6.62591 3.23169 6.69272C3.2565 6.76863 3.32271 6.83452 3.45435 6.96616L6.28931 9.80112C6.42125 9.93306 6.48766 9.99902 6.56372 10.0238C6.63067 10.0455 6.70283 10.0455 6.76978 10.0238C6.84583 9.99901 6.91226 9.93304 7.04419 9.80112L12.8792 3.96616C12.8923 3.95298 12.9034 3.93903 12.9153 3.92709C13.3941 4.84688 13.6663 5.89168 13.6663 7.00034C13.6661 10.682 10.6819 13.6662 7.00024 13.6664C3.31845 13.6664 0.333428 10.6821 0.333252 7.00034C0.333252 3.31844 3.31835 0.333344 7.00024 0.333344Z"
                    fill="#374053"
                  />
                </svg>
                <p class="justify-center text-sm font-normal text-steel-900">
                  {{ item }}
                </p>
              </div>
              <div
                v-if="index !== currentStep?.checkList.length - 1"
                class="h-0 w-full outline outline-1 outline-offset-[-0.50px] outline-gray-100"
              ></div>
            </div>
          </div>
          <form
            class="flex w-full flex-col gap-4"
            @submit.prevent="currentStep?.primaryButton.action"
          >
            <div
              v-if="currentStep?.title === 'Claim ELD Exemption'"
              class="w-full"
            >
              <div
                class="mb-1 inline-flex items-center justify-start gap-0.5 self-stretch px-1"
              >
                <div
                  class="justify-start text-xs font-semibold leading-none tracking-tight text-steel-800"
                >
                  Exemption Reason
                </div>
                <div
                  class="justify-start font-['Poppins'] text-xs font-semibold leading-none tracking-tight text-rose-700"
                >
                  *
                </div>
              </div>
              <DropdownSelectComponent
                source="exemptReasons"
                :dropdown-list="currentStep?.dropdownList"
                :selected-item-id="selectedExemptReason?.id || 'none'"
                @select-item="(_, item) => handleExemptReasonSelect(item)"
              />
            </div>
            <div
              v-if="
                currentStep?.title === 'Claim ELD Exemption' &&
                !showAddressForm &&
                isDocumentUploadEnabled &&
                (selectedExemptReason?.id ===
                  'driver_does_not_travel_over_150_miles' ||
                  selectedExemptReason?.id === 'vehicle_has_glider_kit')
              "
              class="flex w-full max-w-[475px] flex-col gap-4"
            >
              <div>
                <p class="text-sm font-medium text-steel-1100">
                  {{ uploadDocumentsForm?.title }}
                </p>
                <p class="text-xs text-steel-700">
                  {{ uploadDocumentsForm?.description }}
                </p>

                <div class="mt-4">
                  <ol class="list-inside list-disc text-xs text-steel-900">
                    <li
                      v-for="bulletPoint in uploadDocumentsForm?.bulletPoints"
                      :key="bulletPoint"
                    >
                      {{ bulletPoint }}
                    </li>
                  </ol>
                </div>
              </div>
              <div
                class="dropzone flex flex-col items-center justify-center border border-dashed bg-steel-100 text-gray-400"
                :class="{
                  'border-yellow-500 bg-yellow-200 text-yellow-500':
                    fileOverDropArea,
                  'border-gray-200': !fileOverDropArea,
                }"
                @drop.prevent="dropFiles"
                @dragover.prevent
                @dragenter.prevent="fileOverDropArea = true"
                @dragleave.prevent="fileOverDropArea = false"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M20.0306 7.71938L14.7806 2.46938C14.7109 2.39975 14.6282 2.34454 14.5371 2.3069C14.4461 2.26926 14.3485 2.24992 14.25 2.25H5.25C4.85218 2.25 4.47064 2.40804 4.18934 2.68934C3.90804 2.97064 3.75 3.35218 3.75 3.75V20.25C3.75 20.6478 3.90804 21.0294 4.18934 21.3107C4.47064 21.592 4.85218 21.75 5.25 21.75H18.75C19.1478 21.75 19.5294 21.592 19.8107 21.3107C20.092 21.0294 20.25 20.6478 20.25 20.25V8.25C20.2501 8.15148 20.2307 8.05391 20.1931 7.96286C20.1555 7.87182 20.1003 7.78908 20.0306 7.71938ZM15 4.81031L17.6897 7.5H15V4.81031ZM18.75 20.25H5.25V3.75H13.5V8.25C13.5 8.44891 13.579 8.63968 13.7197 8.78033C13.8603 8.92098 14.0511 9 14.25 9H18.75V20.25ZM14.7806 12.9694C14.8503 13.0391 14.9056 13.1218 14.9433 13.2128C14.981 13.3039 15.0004 13.4015 15.0004 13.5C15.0004 13.5985 14.981 13.6961 14.9433 13.7872C14.9056 13.8782 14.8503 13.9609 14.7806 14.0306C14.7109 14.1003 14.6282 14.1556 14.5372 14.1933C14.4461 14.231 14.3485 14.2504 14.25 14.2504C14.1515 14.2504 14.0539 14.231 13.9628 14.1933C13.8718 14.1556 13.7891 14.1003 13.7194 14.0306L12.75 13.0603V17.25C12.75 17.4489 12.671 17.6397 12.5303 17.7803C12.3897 17.921 12.1989 18 12 18C11.8011 18 11.6103 17.921 11.4697 17.7803C11.329 17.6397 11.25 17.4489 11.25 17.25V13.0603L10.2806 14.0306C10.2109 14.1003 10.1282 14.1556 10.0372 14.1933C9.94613 14.231 9.84855 14.2504 9.75 14.2504C9.65145 14.2504 9.55387 14.231 9.46283 14.1933C9.37178 14.1556 9.28906 14.1003 9.21937 14.0306C9.14969 13.9609 9.09442 13.8782 9.0567 13.7872C9.01899 13.6961 8.99958 13.5985 8.99958 13.5C8.99958 13.4015 9.01899 13.3039 9.0567 13.2128C9.09442 13.1218 9.14969 13.0391 9.21937 12.9694L11.4694 10.7194C11.539 10.6496 11.6217 10.5943 11.7128 10.5566C11.8038 10.5188 11.9014 10.4994 12 10.4994C12.0986 10.4994 12.1962 10.5188 12.2872 10.5566C12.3783 10.5943 12.461 10.6496 12.5306 10.7194L14.7806 12.9694Z"
                    fill="#374053"
                  />
                </svg>
                <p class="text-xs font-medium text-steel-900">
                  Drag and Drop or
                  <label
                    for="files-upload"
                    class="cursor-pointer text-sun-700 underline hover:opacity-80 active:opacity-70"
                  >
                    Upload
                  </label>
                  file
                </p>
                <input
                  id="files-upload"
                  type="file"
                  multiple
                  @change="onFilesUpload"
                />
              </div>

              <div
                v-if="selectedFiles.length > 0"
                class="flex w-full flex-col gap-2"
              >
                <h3 class="text-sm font-medium text-steel-1100">
                  Selected Files
                </h3>
                <div class="max-h-40 overflow-y-auto">
                  <div
                    v-for="(file, index) in selectedFiles"
                    :key="index"
                    class="mb-1 flex items-center justify-between rounded-md border p-2"
                    style="border-color: rgba(31, 36, 46, 0.14)"
                  >
                    <div class="flex w-full items-center justify-between gap-2">
                      <span class="truncate text-xs font-normal text-gray-900">
                        {{ file.name }}
                      </span>
                    </div>
                    <svg
                      v-if="!file.uploaded && !file.uploading"
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      class="cursor-pointer hover:opacity-70"
                      @click="removeFile(index)"
                    >
                      <path
                        d="M15.3033 5.87521L14.1247 4.6967L9.99996 8.82149L5.87517 4.6967L4.69666 5.87521L8.82144 10L4.69666 14.1248L5.87517 15.3033L9.99996 11.1785L14.1247 15.3033L15.3033 14.1248L11.1785 10L15.3033 5.87521Z"
                        fill="#6C7C9D"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="currentStep?.id === 'assignDriver'">
              <div
                class="inline-flex items-center justify-start gap-0.5 self-stretch px-1"
              >
                <label
                  for="name"
                  class="justify-start text-xs font-semibold leading-none tracking-tight text-steel-800"
                >
                  Driver's Name (Required)
                </label>
                <div
                  class="justify-start font-['Poppins'] text-xs font-semibold leading-none tracking-tight text-rose-700"
                >
                  *
                </div>
              </div>
              <InputComponent
                pattern="^[a-zA-Z\s]+$"
                title="Please enter a valid name (letters only, no numbers or special characters)"
                min-length="2"
                wrapper-class="text-xs h-8 flex justify-start items-center px-2"
                :has-error="
                  showInputErrorBorders && assignDriverNameInput === ''
                "
                :is-custom-text-size="true"
                input-label="name"
                placeholder="Name..."
                @input-change="handleAssignDriverNameInputChange"
              />
            </div>
            <div v-if="currentStep?.id === 'assignDriver'">
              <div
                class="inline-flex items-center justify-start gap-0.5 self-stretch px-1"
              >
                <label
                  for="phone"
                  class="justify-start text-xs font-semibold leading-none tracking-tight text-steel-800"
                >
                  Driver's Phone (Required)
                </label>
                <div
                  class="justify-start font-['Poppins'] text-xs font-semibold leading-none tracking-tight text-rose-700"
                >
                  *
                </div>
              </div>
              <FormField
                id="phone"
                ref="formFieldRef"
                label=""
                :value="assignDriverPhoneInput"
                placeholder="Enter Mobile #"
                :on-input="handleAssignDriverPhoneInputChange"
                :on-blur="handlePhoneFieldBlur"
                name="phone_number"
                type="tel"
                class="text-dark-blue"
                :error="phoneError"
                :auto-focus="true"
                :minimal-style="true"
                :removeYMargin="true"
                custom-input-text-class="text-xs"
                custom-input-class="h-8"
                :is-border-red="
                  showInputErrorBorders && !assignDriverPhoneInput
                "
                border-radius-class="rounded-lg"
                @country-changed="handleCountrySelectChange"
              />
            </div>
            <div
              class="max-w-[475px]"
              v-if="showAddressForm"
            >
              <p class="text-sm font-medium text-steel-1100">
                {{ uploadDocumentsForm?.title }}
              </p>
              <p class="text-xs text-steel-700">
                {{ uploadDocumentsForm?.description }}
              </p>
            </div>
            <div
              class="max-w-[475px]"
              v-if="showAddressForm"
            >
              <div
                class="inline-flex items-center justify-start gap-0.5 self-stretch px-1"
              >
                <label
                  for="street"
                  class="justify-start text-xs font-semibold leading-none tracking-tight text-steel-800"
                >
                  Address (Required)
                </label>
                <div
                  class="justify-start font-['Poppins'] text-xs font-semibold leading-none tracking-tight text-rose-700"
                >
                  *
                </div>
              </div>
              <InputComponent
                pattern="^(?=.*\d)(?=.*[a-zA-Z]).*$"
                title="Please enter a valid street address ( 123 Main St, Apt 4B)"
                min-length="3"
                wrapper-class="text-xs h-8 flex justify-start items-center px-2"
                :is-custom-text-size="true"
                input-label="street"
                placeholder="Address..."
                @input-change="(streetInput) => (street = streetInput)"
              />
            </div>
            <div
              class="max-w-[475px]"
              v-if="showAddressForm"
            >
              <div
                class="inline-flex items-center justify-start gap-0.5 self-stretch px-1"
              >
                <label
                  for="city"
                  class="justify-start text-xs font-semibold leading-none tracking-tight text-steel-800"
                >
                  City
                </label>
                <div
                  class="justify-start font-['Poppins'] text-xs font-semibold leading-none tracking-tight text-rose-700"
                >
                  *
                </div>
              </div>
              <InputComponent
                pattern="[A-Za-z\s]+"
                title="Please enter a valid city name (letters and spaces only)"
                min-length="2"
                max-length="50"
                wrapper-class="text-xs h-8 flex justify-start items-center px-2"
                :is-custom-text-size="true"
                input-label="city"
                placeholder="City..."
                @input-change="(cityInput) => (city = cityInput)"
              />
            </div>
            <div
              v-if="showAddressForm"
              class="flex w-full max-w-[475px] items-center gap-10"
            >
              <div class="h-12 w-fit">
                <div
                  class="inline-flex items-center justify-start gap-0.5 px-2"
                >
                  <div
                    for="state-select"
                    class="justify-start text-xs font-semibold leading-none tracking-tight text-steel-800"
                  >
                    State
                  </div>
                  <div
                    class="justify-start font-['Poppins'] text-xs font-semibold leading-none tracking-tight text-rose-700"
                  >
                    *
                  </div>
                </div>
                <select
                  v-model="state"
                  class="default-input-wrapper-border hover:bg-info-subtle-bg custom-select flex h-8 items-center gap-2 rounded-lg bg-steel-100 px-6 text-xs outline-1 outline-offset-[-1px]"
                  name="state-select"
                >
                  <option
                    key="none"
                    selected
                    value="none"
                  >
                    ...
                  </option>
                  <option
                    v-for="usState in Object.keys(usStates)"
                    :key="usState"
                    :value="usState"
                  >
                    {{ usState }}
                  </option>
                </select>
              </div>
              <div class="h-12 w-fit">
                <div
                  class="inline-flex items-center justify-start gap-0.5 self-stretch px-1"
                >
                  <div
                    class="justify-start text-xs font-semibold leading-none tracking-tight text-steel-800"
                  >
                    Zip
                  </div>
                  <div
                    class="justify-start font-['Poppins'] text-xs font-semibold leading-none tracking-tight text-rose-700"
                  >
                    *
                  </div>
                </div>
                <InputComponent
                  pattern="^\d{5}(?:-\d{4}|\s\d{4})?$"
                  title="Enter a 5-digit ZIP code or a 5-digit plus 4-digit extended ZIP code (e.g., 12345 or 12345-6789)"
                  wrapper-class="text-xs h-8 flex justify-start items-center px-2"
                  :is-custom-text-size="true"
                  input-label="street-address-1"
                  placeholder="Zip..."
                  @input-change="(zipInput) => (zip = zipInput)"
                />
              </div>
            </div>
            <div v-if="currentStep?.id === 'relayPickupTime'">
              <div
                class="inline-flex items-center justify-start gap-0.5 self-stretch px-1"
              >
                <label
                  for="date"
                  class="justify-start text-xs font-semibold leading-none tracking-tight text-steel-800"
                >
                  Date
                </label>
                <!-- <div
                class="justify-start font-['Poppins'] text-xs font-semibold leading-none tracking-tight text-rose-700"
              >
                *
              </div> -->
              </div>
              <div class="detail-field-date relative w-52">
                <DetailFieldDate
                  label="Pickup Date"
                  :stop="origin"
                  @set-pickup-date="handleSetPickupDate"
                />
              </div>
            </div>
            <div v-if="currentStep?.id === 'relayPickupTime'">
              <div
                class="inline-flex items-center justify-start gap-0.5 self-stretch px-1"
              >
                <label
                  for="time"
                  class="justify-start text-xs font-semibold leading-none tracking-tight text-steel-800"
                >
                  Timezone
                </label>
                <!-- <div
                class="justify-start font-['Poppins'] text-xs font-semibold leading-none tracking-tight text-rose-700"
              >
                *
              </div> -->
              </div>
              <select
                v-model="selectedTimezone"
                class="default-input-wrapper-border custom-select flex h-8 items-center gap-2 rounded-lg bg-steel-100 px-6 text-sm text-xs outline-1 outline-offset-[-1px]"
                name="state-select"
              >
                <option
                  v-for="timezone in timezones"
                  :key="timezone.region"
                  :value="timezone.region"
                  class="flex items-center justify-center text-sm"
                >
                  {{ timezone.label }}
                </option>
              </select>
            </div>
            <div
              v-if="
                showInput &&
                (currentStep?.id === 'assignVehicle' ||
                  currentStep?.id === 'assignRelayVehicle')
              "
              class="flex flex-col gap-2"
            >
              <!-- loading vehicles -->
              <div
                v-if="isLoadingVehicleList"
                class="mx-auto h-16 w-16 text-center"
              >
                <img
                  class="loading relative w-full"
                  src="@/assets/loading-v2-yellow.svg"
                  alt="loading"
                />
              </div>
              <!-- input search -->
              <!-- picklist -->
              <div
                v-if="
                  !isLoadingVehicleList &&
                  hasAllVehiclesLoaded &&
                  allVehicles.length === 0
                "
                class="text-rose-800"
              >
                No vehicles found.
              </div>
              <div
                v-else-if="!isLoadingVehicleList && allVehicles.length"
                class="relative mt-1 flex h-full w-full flex-col gap-3"
                @click.stop
              >
                <InputComponent
                  input-label="assignVehicleModal"
                  wrapper-class="flex w-full text-xs items-center gap-1.5 p-2 my-2"
                  placeholder="Search Vehicles"
                  :value="currentStep?.input"
                  @input-change="currentStep?.inputChange"
                  @enter-press="currentStep?.enterPress"
                >
                  <template #leftIcon>
                    <img
                      class="pt-1"
                      src="@/assets/search-light-gray.svg"
                      alt="filters"
                    />
                  </template>
                </InputComponent>
                <button
                  type="button"
                  class="flex min-w-fit items-center justify-start font-['Poppins'] text-xs font-medium leading-none tracking-tight text-yellow-700 underline hover:text-yellow-800 active:text-yellow-900"
                  @click="$emit('openViewEquipmentModal')"
                >
                  I don't see my vehicle
                </button>
                <ul
                  v-if="isDesktop"
                  aria-labelledby="combobox-button"
                  role="listbox"
                  class="min-h-[100px] w-full overflow-scroll rounded-md bg-white py-1"
                  :class="
                    assignedVehicle?.id ? 'max-h-[200px]' : 'max-h-[200px]'
                  "
                >
                  <!-- name="selectedVehicleDropdown" -->
                  <RadioButtonDesktopComponent
                    v-for="vehicle in currentStep?.vehiclesList"
                    :key="vehicle.id"
                    :item="vehicle.identifier || ''"
                    :subtext="vehicle.vin"
                    :value="vehicle.id"
                    :show-background-when-selected="true"
                    :show="true"
                    :show-checkmark="false"
                    :show-border-on-hover="false"
                    :checked="
                      currentStep?.id === 'assignRelayVehicle'
                        ? vehicle.id === selectedRelayVehicle?.id
                        : vehicle.id === selectedVehicle?.id
                    "
                    :handle-click="() => currentStep?.radioButtonClick(vehicle)"
                  >
                    <template #item-icon>
                      <img
                        v-if="vehicle.isEldActive"
                        class="h-4 w-4"
                        src="@/assets/eld-connected-icon.svg"
                        alt="ELD Active"
                      />
                      <img
                        v-else-if="vehicle.isEldExempt"
                        class="h-4 w-4"
                        src="@/assets/eld-exempt-icon.svg"
                        alt="ELD Exempt"
                      />
                      <img
                        v-else
                        class="h-4 w-4"
                        src="@/assets/eld-not-connected-icon.svg"
                        alt="ELD Inactive"
                      />
                    </template>
                  </RadioButtonDesktopComponent>
                </ul>
                <ul
                  v-else
                  aria-labelledby="combobox-button"
                  role="listbox"
                  class="max-h-[200px] min-h-[100px] w-full overflow-scroll rounded-md bg-white py-1"
                >
                  <!-- name="selectedVehicleDropdown" -->

                  <RadioButtonMobileComponent
                    v-for="vehicle in currentStep?.vehiclesList"
                    :key="vehicle.id"
                    :item="vehicle.identifier || ''"
                    :subtext="vehicle.vin"
                    :value="vehicle.id"
                    :show-background-when-selected="true"
                    :show="true"
                    :show-checkmark="false"
                    :show-border-on-hover="false"
                    :checked="
                      currentStep?.id === 'assignRelayVehicle'
                        ? vehicle.id === selectedRelayVehicle?.id
                        : vehicle.id === selectedVehicle?.id
                    "
                    :handle-click="() => currentStep?.radioButtonClick(vehicle)"
                  >
                    <template #item-icon>
                      <img
                        v-if="vehicle.isEldActive"
                        class="h-4 w-4"
                        src="@/assets/eld-connected-icon.svg"
                        alt="ELD Active"
                      />
                      <img
                        v-else-if="vehicle.isEldExempt"
                        class="h-4 w-4"
                        src="@/assets/eld-exempt-icon.svg"
                        alt="ELD Exempt"
                      />
                      <img
                        v-else
                        class="h-4 w-4"
                        src="@/assets/eld-not-connected-icon.svg"
                        alt="ELD Inactive"
                      />
                    </template>
                  </RadioButtonMobileComponent>
                </ul>
              </div>
            </div>
            <div v-if="currentStep?.id === 'eldExemptAssignDriver'">
              <div v-if="isDriversListLoading">
                <div class="mx-auto h-16 w-16 text-center">
                  <img
                    class="loading relative w-full"
                    src="@/assets/loading-v2-yellow.svg"
                    alt="loading"
                  />
                </div>
              </div>
              <div
                class="h-full"
                v-else
              >
                <ul
                  aria-labelledby="combobox-button"
                  role="listbox"
                  class="min-h-[100px] w-full rounded-md bg-white py-1"
                >
                  <RadioButtonDesktopComponent
                    v-if="isDesktop"
                    item="Select an existing driver"
                    value="select-driver"
                    :show="true"
                    :disabled="driversList.length < 2"
                    :show-background-when-selected="false"
                    :show-checkmark="false"
                    :show-border-on-hover="false"
                    :checked="!isAddDriver"
                    :handle-click="() => (isAddDriver = false)"
                  />
                  <RadioButtonMobileComponent
                    v-else
                    item="Select an existing driver"
                    value="select-driver"
                    :disabled="driversList.length < 2"
                    :show-background-when-selected="false"
                    :show="true"
                    :show-checkmark="false"
                    :show-border-on-hover="false"
                    :checked="!isAddDriver"
                    :handle-click="() => (isAddDriver = false)"
                  />
                  <transition
                    enter-active-class="transition duration-25 ease-out"
                    enter-from-class="transform scale-95 opacity-0"
                    enter-to-class="transform scale-100 opacity-100"
                    leave-active-class="transition duration-75 ease-out"
                    leave-from-class="transform scale-100 opacity-100"
                    leave-to-class="transform scale-95 opacity-0"
                  >
                    <div
                      class="w-full px-1"
                      v-if="!isAddDriver && driversList.length > 1"
                    >
                      <DropdownSelectComponent
                        source="driversList"
                        :dropdown-list="driversList"
                        :selected-item-id="
                          eldExemptSelectedDriver?.id || 'none'
                        "
                        @select-item="
                          (_, item) => (eldExemptSelectedDriver = item)
                        "
                      />
                    </div>
                  </transition>
                  <RadioButtonDesktopComponent
                    v-if="isDesktop"
                    item="Add a new driver"
                    value="add-driver"
                    :show="true"
                    :show-checkmark="false"
                    :show-border-on-hover="false"
                    :show-background-when-selected="false"
                    :checked="isAddDriver"
                    :handle-click="() => (isAddDriver = true)"
                  />
                  <RadioButtonMobileComponent
                    v-else
                    item="Add a new driver"
                    value="add-driver"
                    :show="true"
                    :show-checkmark="false"
                    :show-border-on-hover="false"
                    :show-background-when-selected="false"
                    :checked="isAddDriver"
                    :handle-click="() => (isAddDriver = true)"
                  />
                  <transition
                    enter-active-class="transition duration-25 ease-out"
                    enter-from-class="transform scale-95 opacity-0"
                    enter-to-class="transform scale-100 opacity-100"
                    leave-active-class="transition duration-75 ease-out"
                    leave-from-class="transform scale-100 opacity-100"
                    leave-to-class="transform scale-95 opacity-0"
                  >
                    <div
                      class="mt-4 flex flex-col gap-4 px-1"
                      v-if="isAddDriver"
                    >
                      <!-- add driver form -->
                      <div class="flex w-full flex-col gap-2">
                        <div
                          class="flex items-center justify-start gap-0.5 self-stretch px-1"
                        >
                          <label
                            for="first-name"
                            class="justify-start text-xs font-semibold leading-none tracking-tight text-steel-800"
                          >
                            First Name (Required)
                          </label>
                          <div
                            class="justify-start font-['Poppins'] text-xs font-semibold leading-none tracking-tight text-rose-700"
                          >
                            *
                          </div>
                        </div>
                        <InputComponent
                          pattern="^[a-zA-Z\s]+$"
                          title="Please enter a valid name (letters only, no numbers or special characters)"
                          min-length="2"
                          wrapper-class="text-xs h-8 flex justify-start items-center px-2"
                          :has-error="
                            showInputErrorBorders &&
                            eldExemptAssignDriverFirstNameInput === ''
                          "
                          :is-custom-text-size="true"
                          input-label="first-name"
                          placeholder="First Name"
                          @input-change="
                            handleEldExemptAssignDriverFirstNameInputChange
                          "
                        />
                      </div>
                      <div class="flex flex-col gap-2">
                        <div
                          class="flex items-center justify-start gap-0.5 self-stretch px-1"
                        >
                          <label
                            for="last-name"
                            class="justify-start text-xs font-semibold leading-none tracking-tight text-steel-800"
                          >
                            Last Name (Required)
                          </label>
                          <div
                            class="justify-start font-['Poppins'] text-xs font-semibold leading-none tracking-tight text-rose-700"
                          >
                            *
                          </div>
                        </div>
                        <InputComponent
                          pattern="^[a-zA-Z\s]+$"
                          title="Please enter a valid name (letters only, no numbers or special characters)"
                          min-length="2"
                          wrapper-class="text-xs h-8 flex justify-start items-center px-2"
                          :has-error="
                            showInputErrorBorders &&
                            eldExemptAssignDriverLastNameInput === ''
                          "
                          :is-custom-text-size="true"
                          input-label="last-name"
                          placeholder="Last Name"
                          @input-change="
                            handleEldExemptAssignDriverLastNameInputChange
                          "
                        />
                      </div>
                      <div class="flex flex-col gap-2">
                        <div
                          class="inline-flex items-center justify-start gap-0.5 self-stretch px-1"
                        >
                          <label
                            for="email"
                            class="justify-start text-xs font-semibold leading-none tracking-tight text-steel-800"
                          >
                            Driver's Email (Required)
                          </label>
                          <div
                            class="justify-start text-xs font-semibold leading-none tracking-tight text-rose-700"
                          >
                            *
                          </div>
                        </div>
                        <FormField
                          id="email"
                          label=""
                          :value="eldExemptAssignDriverEmailInput"
                          placeholder="Enter Email Address"
                          :on-input="
                            handleEldExemptAssignDriverEmailInputChange
                          "
                          :on-blur="handleEldExemptAssignDriverEmailInputBlur"
                          name="email"
                          class="text-dark-blue"
                          :error="eldExemptEmailError"
                          :auto-focus="true"
                          :minimal-style="true"
                          :removeYMargin="true"
                          custom-input-text-class="text-xs"
                          custom-input-class="h-8 px-2"
                          :is-border-red="
                            showInputErrorBorders &&
                            !eldExemptAssignDriverEmailInput
                          "
                          border-radius-class="rounded-lg"
                        />
                      </div>
                      <div class="flex flex-col gap-2">
                        <div
                          class="inline-flex items-center justify-start gap-0.5 self-stretch px-1"
                        >
                          <label
                            for="phone"
                            class="justify-start text-xs font-semibold leading-none tracking-tight text-steel-800"
                          >
                            Driver's Phone (Required)
                          </label>
                          <div
                            class="justify-start font-['Poppins'] text-xs font-semibold leading-none tracking-tight text-rose-700"
                          >
                            *
                          </div>
                        </div>
                        <FormField
                          id="phone"
                          ref="eldExemptFormFieldRef"
                          label=""
                          :value="eldExemptAssignDriverPhoneInput"
                          placeholder="Enter Mobile #"
                          :on-input="
                            handleEldExemptAssignDriverPhoneInputChange
                          "
                          :on-blur="handleEldExemptPhoneFieldBlur"
                          name="phone_number"
                          type="tel"
                          class="text-dark-blue"
                          :error="eldExemptPhoneError"
                          :auto-focus="true"
                          :minimal-style="true"
                          :removeYMargin="true"
                          custom-input-text-class="text-xs"
                          custom-input-class="h-8"
                          :is-border-red="
                            showInputErrorBorders &&
                            !eldExemptAssignDriverPhoneInput
                          "
                          border-radius-class="rounded-lg"
                          @country-changed="
                            handleEldExemptAssignDriverCountrySelectChange
                          "
                        />
                      </div>
                    </div>
                  </transition>
                </ul>
              </div>
            </div>
            <div v-if="currentStep?.id === 'assignTrailer'">
              <div
                class="inline-flex items-center justify-start gap-0.5 self-stretch px-1"
              >
                <label
                  for="trailer"
                  class="justify-start text-xs font-semibold leading-none tracking-tight text-steel-800"
                >
                  Trailer Number
                </label>
              </div>
              <InputComponent
                pattern="^[a-zA-Z0-9]+$"
                title="Please enter a valid trailer name (no special characters)"
                min-length="1"
                max-length="20"
                wrapper-class="text-xs h-8 flex justify-start items-center px-2"
                :is-custom-text-size="true"
                input-label="trailer"
                placeholder="Trailer Number..."
                @input-change="handleAssignTrailerNumberInputChange"
              />
            </div>
            <div
              v-if="currentStep?.id === 'rateConfirmationTerms'"
              class="inline-flex max-w-[475px] items-center justify-center gap-2 self-stretch rounded-[5px] bg-steel-100 p-4 outline outline-1 outline-offset-[-1px] outline-neutral-300-a"
            >
              <p
                class="flex-1 justify-start font-['Poppins'] text-sm font-normal leading-5 tracking-tight text-steel-800"
              >
                {{ currentStep?.disclaimer }}
              </p>
            </div>
            <div
              v-if="currentStep?.checkboxLabel && !showAddressForm"
              class="align-left flex w-full max-w-[475px] items-center justify-center gap-2 self-stretch rounded-[5px] bg-steel-100 p-4 outline outline-1 outline-offset-[-1px] outline-neutral-300-a"
            >
              <CustomCheckbox
                :item="{
                  label: currentStep?.checkboxLabel,
                }"
                text-size="text-xs"
                :is-selected="currentStep?.checkbox"
                @toggle="currentStep?.checkboxClick"
              />
            </div>
            <div
              v-if="!isDesktop && showInput"
              class="flex items-center justify-between gap-2 self-stretch"
            >
              <MyLoadsDesktopButtonComponent
                v-if="currentStep?.secondaryButton"
                :button-class="`flex-1 ${currentStep?.secondaryButton.style} rounded-[3px] px-3 py-1.5`"
                :button-text="currentStep?.secondaryButton.text"
                button-type="button"
                text-class="text-sm font-medium leading-10 tracking-tight"
                @button-click="currentStep?.secondaryButton.action"
              />
              <div
                @mouseenter="
                  () => {
                    showInputErrorBorders = true;
                  }
                "
                @mouseleave="
                  () => {
                    showInputErrorBorders = false;
                  }
                "
                class="flex-1"
              >
                <MyLoadsDesktopButtonComponent
                  v-if="currentStep?.primaryButton"
                  button-class="w-full primary-button rounded-[3px] px-3 py-1.5"
                  :is-disabled="currentStep?.primaryButton.isDisabled"
                  :button-text="currentStep?.primaryButton.text"
                  button-type="submit"
                  text-class="text-sm font-medium leading-10 tracking-tight"
                />
              </div>
            </div>
            <div
              v-else-if="showInput"
              class="flex items-center justify-end gap-2 self-stretch"
            >
              <MyLoadsDesktopButtonComponent
                v-if="currentStep?.secondaryButton"
                :button-class="`${currentStep?.secondaryButton.style} rounded-[5px] px-3 py-1 h-8`"
                text-class="font-medium text-sm leading-10 tracking-tight"
                :button-text="currentStep?.secondaryButton.text"
                @button-click="currentStep?.secondaryButton.action"
              />
              <div
                @mouseenter="
                  () => {
                    showInputErrorBorders = true;
                  }
                "
                @mouseleave="
                  () => {
                    showInputErrorBorders = false;
                  }
                "
              >
                <MyLoadsDesktopButtonComponent
                  v-if="currentStep?.primaryButton"
                  button-class="primary-button rounded-[5px] px-3 py-1 h-8"
                  text-class="font-medium text-sm leading-10 tracking-tight"
                  button-type="submit"
                  :is-disabled="currentStep?.primaryButton.isDisabled"
                  :button-text="currentStep?.primaryButton.text"
                />
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.show-scroll {
  overflow: scroll;
  scrollbar-width: 2px;
}
/* .show-scroll::-webkit-scrollbar {
  display: block;
  scrollbar-width: 2px;
} */
.custom-select {
  outline: none;
  border: none;
  border-color: transparent;
  outline: 1px solid rgba(31, 36, 46, 0.14);
  outline-offset: -1px;
  background-color: rgba(246, 247, 249, 1);
  -webkit-box-shadow: none;
  -moz-box-shadow: none;
  box-shadow: none;
  flex-grow: 1;
  line-height: 1.25;
  letter-spacing: -0.025em;
  font-weight: 400;
  color: #374053;
}
.default-input-wrapper-border {
  outline: 1px solid rgba(31, 36, 46, 0.14);
}
.info-block {
  min-width: 200px;
  width: max-content;
}
</style>
